'use client';

import { getPortalApiBase } from '@korvo/core/ai/portal/threads-client'
import { useAuth } from '@korvo/core/client/hooks/useAuth'
import { useCurrentUserProfile } from '@korvo/core/client/hooks/useCurrentUserProfile'
import { useIsAdminForMessages } from '@korvo/core/client/hooks/useIsAdminForMessages'
import {
  CHAT_EMPTY_RESPONSE_THROW_ID,
  ensureKorvoErrorForChat,
  formatChatClientErrorMessage,
} from '@korvo/core/lib/chat-client-error-message'
import { ERR_BAD_REQUEST_400, ERR_CHAT_500, ERR_EDITOR_THREADS_LOAD, ERR_INTERNAL_500 } from '@korvo/core/lib/error-codes'
import { isThreadHistoryNotFoundError } from '@korvo/core/lib/is-thread-history-not-found-error'
import { getErrorIdEntry } from '@korvo/core/lib/error-ids'
import { KorvoError } from '@korvo/core/lib/korvo-error'
import { MessageNormalizer, type RawMessage } from '@korvo/portal-sdk/message-normalizer'
import Image from 'next/image';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import invariant from 'tiny-invariant'

import { ToolCallMessage } from '../Chat/components/ToolCallMessage'
import { ModuleIcon } from '../Icon/ModuleIcon'
import { LoadingSpinner } from '../LoadingSpinner'
import { UserAvatarBubble } from '../UserAvatarBubble'
import { AgentMessage, isAgentMessage } from './components/AgentMessage'
import { ChatMessageBodyContent } from './components/ChatMessageBodyContent'
import { isHiddenUserMessage as isHiddenUserMessageUtil } from './hidden-message'
import { ensureKorvoChatThreadId } from './korvo-chat-ensure-thread'
import { mergeThreadMessagesPreservingClientErrors } from './preserve-client-error-messages'
import { runKorvoChatMessageDebugFlow } from './korvo-chat-message-debug'
import { summarizeToolCallResultForUi } from './korvo-chat-tool-result-summary'
import { ChatSessionSchema } from './schemas'
import { createThreadsClient } from './threads-client-factory'
import { type ChatMessage, type ChatSession, type KorvoChatProps, type ToolCall, type ToolResult } from './types'
import { useKorvoChatDocumentKeydown } from './use-korvo-chat-document-keydown'
import { useKorvoChatMessageEditing } from './use-korvo-chat-message-editing'

type ToolResultPayload = {
  success: boolean
  data?: unknown
  error?: string
}

function isToolResultPayload(value: unknown): value is ToolResultPayload {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { success?: unknown }).success === 'boolean'
  )
}

type ClientToolEventPayload = {
  callId?: string
  requestId?: string
  toolName?: string
  toolId?: string
  args?: Record<string, unknown>
}

/** Narrowing helper for query/helper flags + optional RAG on SSE-normalized messages. */
type ChatMessageAgentFlags = ChatMessage & {
  isQueryMessage?: boolean
  isHelperMessage?: boolean
  ragResults?: Array<{ content: string; title?: string; similarity?: number; sourceId?: string | number }>
}

type ToolCallWithOpenAiFunction = ToolCall & {
  function?: { name?: string; arguments?: unknown }
}

const KNOWN_CHAT_ROLES: ChatMessage['role'][] = [
  'user',
  'assistant',
  'system',
  'tool_execution',
  'summary_marker',
  'helper',
  'query',
]

/** Stable empty list for controlled sessions with no seed messages — avoids new `[]` each render (workbench session metadata updates). */
const CONTROLLED_SESSION_INITIAL_MESSAGES_EMPTY: ChatMessage[] = []

const parseInitialMessages = (value: unknown): ChatMessage[] => {
  if (!Array.isArray(value)) {
    return []
  }
  return value.reduce<ChatMessage[]>((acc, raw) => {
    if (!raw || typeof raw !== 'object') {
      return acc
    }
    const candidate = raw as Record<string, unknown>
    const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
    const role = typeof candidate.role === 'string' ? candidate.role : ''
    const content = typeof candidate.content === 'string' ? candidate.content : ''
    if (!id || !KNOWN_CHAT_ROLES.includes(role as ChatMessage['role'])) {
      return acc
    }
    // Allow assistant messages with empty content (model may return empty on first turn)
    const timestamp = typeof candidate.timestamp === 'number' && Number.isFinite(candidate.timestamp)
      ? candidate.timestamp
      : Date.now()
    const message: ChatMessage = {
      id,
      role: role as ChatMessage['role'],
      content,
      timestamp,
    }
    if (candidate.metadata && typeof candidate.metadata === 'object') {
      message.metadata = candidate.metadata as Record<string, unknown>
    }
    if (Array.isArray(candidate.toolCalls) && candidate.toolCalls.length > 0) {
      message.toolCalls = candidate.toolCalls as ToolCall[]
    }
    acc.push(message)
    return acc
  }, [])
}

/** `Array#findLastIndex` needs lib es2023+; base tsconfig uses es2020. */
function arrayFindLastIndex<T>(items: readonly T[], predicate: (item: T, index: number) => boolean): number {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (predicate(items[i], i)) {
      return i
    }
  }
  return -1
}

/** `Array#findLast` needs lib es2023+; base tsconfig uses es2020. */
function arrayFindLast<T>(items: readonly T[], predicate: (item: T, index: number) => boolean): T | undefined {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (predicate(items[i], i)) {
      return items[i]
    }
  }
  return undefined
}

// Icons (you can replace with your preferred icon library)
const InfoIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const CopyIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
)

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
)



/**
 * KorvoChat - Unified chat component
 * 
 * Consolidates features from core, chat, and mentor modules with:
 * - Simple API integration (no external Portal SDK dependency)
 * - Chat module UX (scrolling, input focus)
 * - Mentor features (debug panel, typing dots)
 * - Mobile responsiveness
 * - Full compliance logging
 */
export function KorvoChat({
  config,
  className = '',
  style,
  readOnly = false,
  session,
  initialSession,
  viewportHeight,
  onMessageSent,
  onAssistantMessage,
  onToolExecutionComplete,
  onSessionChange,
  onError,
  onThreadHistoryNotFound,
  isInitializing = false,
  threadsClient: providedThreadsClient,
  initialHiddenMessage,
  hideComposer = false,
  onRegisterSend,
  showDebug: showDebugProp,
  onDebugChange,
}: KorvoChatProps) {
  const apiEndpoint = config?.portal?.apiEndpoint
  if (!apiEndpoint || String(apiEndpoint).trim().length === 0) {
    throw new KorvoError(ERR_CHAT_500, 1, {
      message: 'KorvoChat requires config.portal.apiEndpoint (e.g. "/api/mentor"). The module registry is not available in client components.',
      status: 500,
    })
  }

  const isSessionControlled = session !== undefined
  const isReadOnly = readOnly === true

  // Initialize ThreadsClient - use provided one or create from config
  const threadsClient = useMemo(() => {
    return providedThreadsClient || createThreadsClient(config)
  }, [providedThreadsClient, config])
  
  // Validate initialSession (external data) using Zod - runs in production
  // Parse, don't validate: gracefully handle invalid data instead of crashing
  let validatedInitialSession: ChatSession | null = null
  if (initialSession) {
    try {
      // Guard against undefined schema (module loading issue)
      if (!ChatSessionSchema) {
        throw new KorvoError(ERR_INTERNAL_500, 1, {
          message: 'ChatSessionSchema is not defined - check Zod import and schema definition',
          status: 500,
        })
      }
      const parseResult = ChatSessionSchema.safeParse(initialSession)
      if (!parseResult.success) {
        // Invalid external data - log error and fallback gracefully
        const errorMessages = parseResult.error.issues
          .map(issue => {
            const path = issue.path.length > 0 ? issue.path.join('.') : 'root'
            return `${path}: ${issue.message}`
          })
          .join(', ')
        
        console.error(
          'KorvoChat: Invalid initialSession data from API/database:',
          parseResult.error.format()
        )
        const error = new Error(`Invalid initialSession: ${errorMessages}`)
        onError?.(error)
        // Fallback to null - component handles null gracefully
        validatedInitialSession = null
      } else {
        // Zod schema ensures threadId is string | null (not optional)
        validatedInitialSession = parseResult.data as ChatSession
      }
    } catch (validationError) {
      // Handle schema initialization errors gracefully
      console.error('KorvoChat: Schema validation error:', validationError)
      onError?.(validationError instanceof Error ? validationError : new Error(String(validationError)))
      validatedInitialSession = null
    }
  }
  
  const initialSessionInitialMessages = useMemo(() => {
    if (!validatedInitialSession) {
      return []
    }
    return parseInitialMessages(validatedInitialSession.metadata?.initialMessages)
  }, [validatedInitialSession])
  const visibleInitialSessionMessages = useMemo(
    () =>
      initialSessionInitialMessages.filter(
        (message) => !isHiddenUserMessageUtil(message, config.ui.hiddenUserMessagePattern)
      ),
    [initialSessionInitialMessages, config.ui.hiddenUserMessagePattern]
  )
  const sessionIsControlled = session != null
  const controlledSessionInitialMessagesSeed = sessionIsControlled
    ? session.metadata?.initialMessages
    : undefined
  const controlledSessionInitialMessages = useMemo(() => {
    if (!sessionIsControlled) {
      return CONTROLLED_SESSION_INITIAL_MESSAGES_EMPTY
    }
    const parsed = parseInitialMessages(controlledSessionInitialMessagesSeed)
    return parsed.length === 0 ? CONTROLLED_SESSION_INITIAL_MESSAGES_EMPTY : parsed
  }, [sessionIsControlled, controlledSessionInitialMessagesSeed])
  const [internalSession, setInternalSession] = useState<ChatSession | null>(validatedInitialSession ?? null)
  
  // Track initialSession reference changes in development to warn about unstable references
  // Track the original prop (before validation) to detect unstable references
  const priorInitialSessionPropRef = useRef<ChatSession | null | undefined>(null)
  const initialSessionChangeCountRef = useRef(0)
  
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && initialSession) {
      // Check if the object reference changed (but ID stayed the same) - indicates unstable prop
      if (
        priorInitialSessionPropRef.current !== null &&
        priorInitialSessionPropRef.current !== initialSession &&
        priorInitialSessionPropRef.current?.id === initialSession.id
      ) {
        initialSessionChangeCountRef.current += 1
        if (initialSessionChangeCountRef.current === 1) {
          // Only warn once to avoid console spam
          console.warn(
            'KorvoChat: initialSession object reference changed but id remained the same. ' +
            'This can cause performance issues. Use useState or useMemo to maintain a stable reference:\n' +
            '  const [session] = useState({ id: "...", title: "...", ... })\n' +
            '  <KorvoChat initialSession={session} ... />'
          )
        }
      } else if (priorInitialSessionPropRef.current?.id !== initialSession.id) {
        // ID changed, reset counter
        initialSessionChangeCountRef.current = 0
      }
      priorInitialSessionPropRef.current = initialSession
    }
  }, [initialSession])
  const currentSession = session ?? internalSession
  const hasThreadId = Boolean(currentSession?.threadId)
  const isThreadReady = Boolean(currentSession?.threadId)
  const externalSessionId = session?.id ?? null
  const externalThreadId = session?.threadId ?? null
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [isInputOverLimit, setIsInputOverLimit] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [isThreadLoading, setIsThreadLoading] = useState(false)
  const [internalShowDebug, setInternalShowDebug] = useState(false)
  const isDebugControlled = showDebugProp !== undefined && onDebugChange != null
  const showDebug = isDebugControlled ? showDebugProp : internalShowDebug
  const setShowDebug = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const next = typeof value === 'function' ? value(showDebug) : value
      if (isDebugControlled) onDebugChange(next)
      else setInternalShowDebug(next)
    },
    [isDebugControlled, showDebug, onDebugChange]
  )
  useEffect(() => {
    if (!showDebug || !config.features.debug) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowDebug(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDebug, config.features.debug, setShowDebug]);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null)
  const [editingContent, setEditingContent] = useState('')
  const { handleEditMessage, handleSaveEdit, handleCancelEdit } = useKorvoChatMessageEditing(
    messages,
    setMessages,
    editingMessageId,
    setEditingMessageId,
    editingContent,
    setEditingContent,
  )
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  type DebugFileInfo = {
    name?: string
    size?: number
    type?: string
  }
  type DebugInputData = {
    characterCount?: number
    wordCount?: number
    lineCount?: number
    fileCount?: number
    language?: string
    timezone?: string
    userAgent?: string
    files?: DebugFileInfo[]
  }
  type DebugTransmissionData = {
    sentAt?: string
    apiEndpoint?: string
    method?: string
    contentType?: string
    threadCreated?: boolean
  }
  type DebugTokens = {
    input?: number
    output?: number
    total?: number
  }
  type DebugSettings = {
    temperature?: number
    maxTokens?: number
    streaming?: boolean
  }
  type DebugInfo = {
    messageId?: string
    timestamp?: number
    role?: string
    createdAt?: string
    updatedAt?: string
    threadId?: string
    sessionId?: string
    packageName?: string
    systemName?: string
    inputData?: DebugInputData
    model?: string
    provider?: string
    processingTime?: number
    transmissionData?: DebugTransmissionData
    tokens?: DebugTokens
    costCents?: number
    settings?: DebugSettings
    systemPrompt?: string
    toolCalls?: unknown[]
  }
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null)
  const [showDebugModal, setShowDebugModal] = useState(false)
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null)
  const [isShareLinkCopied, setIsShareLinkCopied] = useState(false)
  const [, setThreadVisibility] = useState<'private' | 'public' | 'org'>('private')
  const [isOffline, setIsOffline] = useState(false)
  const maxMessageLength =
    typeof config?.ui?.maxMessageLength === 'number' && config.ui.maxMessageLength > 0
      ? config.ui.maxMessageLength
      : 1000
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const [isMobileViewport, setIsMobileViewport] = useState(false)
  const [mobileContextMenuOpen, setMobileContextMenuOpen] = useState(false)
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null)
  const mobileMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const check = () => setIsMobileViewport(typeof window !== 'undefined' && window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    if (!mobileContextMenuOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileContextMenuOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [mobileContextMenuOpen])

  // Network state monitoring
  useEffect(() => {
    // Set initial offline state after mount to avoid hydration mismatch
    setIsOffline(!navigator.onLine)
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])
  const [isStreaming, setIsStreaming] = useState(false)
  
  // Sync ref with state for immediate access in handlers
  useEffect(() => {
    isStreamingRef.current = isStreaming
  }, [isStreaming])

  type StreamingSessionStatus = 'in_progress' | 'completed' | 'cancelled'

  type StreamingSession = {
    threadId: string
    clientMessageId: string
    lastEventId: string | null
    updatedAt: number
    status: StreamingSessionStatus
    ownerTabId?: string
  }

  const { profile: currentUserProfile } = useCurrentUserProfile()
  const { isAdmin } = useAuth();
  const isAdminForMessages = useIsAdminForMessages();
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const clientToolCallsInFlight = useRef(new Set<string>())
  const isAtBottomRef = useRef(true)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastMessageCountRef = useRef(0)
  const toolMessageMapRef = useRef<Map<string, string>>(new Map())
  const streamReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null)
  const streamCancelledRef = useRef(false)
  const lastAssistantMessageIdRef = useRef<string | null>(null)
  const streamingSessionRef = useRef<StreamingSession | null>(null)
  const eventsSourceRef = useRef<EventSource | null>(null)
  const tabIdRef = useRef<string>(`tab-${Math.random().toString(36).slice(2)}`)
  const postStreamHydrationTimersRef = useRef<Map<string, number>>(new Map())
  const isStreamingRef = useRef(false) // Track streaming state in ref for immediate access
  const sendInProgressRef = useRef(false) // Synchronous guard against double send (button+Enter, Strict Mode)
  /** Ref to avoid TDZ: useEffects reference send before handleSendMessage is declared below. */
  const handleSendMessageRef = useRef<(overrideContent?: string) => Promise<void>>(() => Promise.resolve())
  /** Refs for document keydown handler — keep effect deps stable (see createKorvoChatDocumentKeydownHandler). */
  const cancelResetConversationRef = useRef<() => void>(() => {})
  const confirmResetConversationRef = useRef<() => void>(() => {})
  const handleResetConversationRef = useRef<() => void>(() => {})
  const setShowDebugRef = useRef<(value: boolean | ((prev: boolean) => boolean)) => void>(() => {})
  /** Ref for resume effect — `attachEventsStream` is declared later in the component body. */
  const attachEventsStreamRef = useRef<(threadId: string, lastEventId: string | null) => void>(() => {})
  /** Ref for resume replay — `executeClientTool` is declared later; resume must not depend on effect deps. */
  const executeClientToolRef = useRef<(eventData: ClientToolEventPayload) => Promise<void>>(async () => {})
  const lastInitializedSessionIdRef = useRef<string | null>(null) // Track last initialized session ID to prevent infinite loops
  const lastInitializedSessionThreadIdRef = useRef<string | null>(null) // Track threadId so we re-init when threadId arrives (e.g. MentorChatClient after POST)
  const focusComposer = useCallback(() => {
    // Don't auto-focus if user is typing in a contentEditable element (like document editor)
    const activeElement = document.activeElement
    if (activeElement && ((activeElement as HTMLElement).isContentEditable || activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      // Check if the active element is NOT our chat input
      if (activeElement !== inputRef.current && !inputRef.current?.contains(activeElement)) {
        // User is typing elsewhere, don't steal focus
        return
      }
    }
    
    setTimeout(() => {
      // Double-check that we're not stealing focus from contentEditable
      const currentActive = document.activeElement
      if (currentActive && (currentActive as HTMLElement).isContentEditable && currentActive !== inputRef.current) {
        return
      }
      inputRef.current?.focus()
    }, 50)
  }, [])

  // Only auto-focus on initial mount, not on every session change
  // This prevents stealing focus when user is typing in document editor
  const hasAutoFocusedRef = useRef(false)
  const lastSentInitialHiddenForThreadIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!hasAutoFocusedRef.current) {
      hasAutoFocusedRef.current = true
      focusComposer()
    }
  }, [focusComposer])

  // Don't auto-focus on session changes - let user explicitly click to focus
  // This prevents focus stealing when document editor triggers re-renders

  // Session persistence removed - component now requires controlled session management
  // Streaming sessions still use localStorage to persist across page reloads
  
  const getStreamingSessionStorageKey = useCallback(
    (threadId: string | null | undefined) =>
      threadId
        ? `korvochat-stream-${config.portal.packageName}-${config.portal.systemName}-${threadId}`
        : null,
    [config.portal.packageName, config.portal.systemName],
  )

  const normalizeToolCall = useCallback((raw: unknown): ToolCall => {
    const rawObj = raw as Record<string, unknown> | null | undefined
    const id = rawObj?.id ?? rawObj?.callId ?? rawObj?.toolCallId ?? `tool-${Date.now()}`
    const name = rawObj?.name ?? rawObj?.toolName ?? (rawObj?.function as Record<string, unknown> | undefined)?.name ?? 'tool'
    const argsSource = rawObj?.arguments ?? (rawObj?.function as Record<string, unknown> | undefined)?.arguments ?? rawObj?.args ?? {}
    const argumentsParsed =
      typeof argsSource === 'string' 
        ? (() => {
            try {
              return JSON.parse(argsSource)
            } catch {
              return {}
            }
          })()
        : argsSource ?? {}
    const resultValue = rawObj?.result
    const resultString =
      typeof resultValue === 'string'
        ? resultValue
        : resultValue !== undefined
          ? JSON.stringify(resultValue)
          : undefined

    return {
      id: String(id),
      name: String(name),
      arguments: (typeof argumentsParsed === 'object' && argumentsParsed !== null && !Array.isArray(argumentsParsed) ? argumentsParsed : {}) as Record<string, unknown>,
      result: resultString,
      success: rawObj?.success as boolean | undefined,
      error: rawObj?.error as string | undefined
    }
  }, [])

  const buildToolCallContent = useCallback((
    toolCall: ToolCall,
    phase: 'start' | 'update',
    extra?: { progress?: number; message?: string }
  ) => {
    const name = toolCall.name
    // Handle placeholder name during loading
    if (name === 'tool_loading') {
      return 'Executing tool...'
    }
    if (phase === 'start') {
      return `Executing tool: ${name}`
    }
    if (typeof toolCall.error === 'string' && toolCall.error.trim() !== '') {
      return `Tool ${name} failed: ${toolCall.error}`
    }
    if (toolCall.success === false) {
      return `Tool ${name} failed.`
    }
    if (toolCall.success) {
      if (toolCall.result) {
        return `Tool ${name} result: ${summarizeToolCallResultForUi(toolCall.result)}`
      }
      return `Tool ${name} completed successfully.`
    }
    if (extra?.progress !== undefined) {
      const base = `Tool ${name} progress: ${extra.progress}%`
      return extra.message ? `${base} – ${extra.message}` : base
    }
    if (toolCall.result) {
      return `Tool ${name} result: ${summarizeToolCallResultForUi(toolCall.result)}`
    }
    return `Tool ${name} update`
  }, [])

  const appendToolCallMessage = useCallback((raw: unknown) => {
    const normalized = normalizeToolCall(raw)
    const messageId = `tool-msg-${normalized.id}`
    
    console.log('🔧 [KorvoChat] appendToolCallMessage called:', {
      toolCallId: normalized.id,
      toolName: normalized.name,
      messageId,
      timestamp: Date.now()
    })
    
    // CRITICAL: Check toolMessageMapRef FIRST to prevent duplicates immediately
    // This ensures we don't add the same tool call twice even if state hasn't updated yet
    if (toolMessageMapRef.current.has(normalized.id)) {
      const existingMessageId = toolMessageMapRef.current.get(normalized.id)
      console.log('🔧 [KorvoChat] Tool message already exists in map, skipping:', {
        toolCallId: normalized.id,
        existingMessageId
      })
      return
    }
    
    // Check if this tool message already exists to prevent duplicates
    // Also check if a tool message with the same tool call ID already exists (from thread history)
    setMessages(prev => {
      const existingMessage = prev.find(msg => msg.id === messageId)
      if (existingMessage) {
        // Message already exists, don't add duplicate
        console.log('🔧 [KorvoChat] Tool message already exists (by messageId), skipping:', messageId)
        // Update map to track it
        toolMessageMapRef.current.set(normalized.id, messageId)
        return prev
      }
      
      // Also check if a tool_execution message with the same tool call ID already exists
      // BUT: Only skip if it's from a previous conversation (not the current streaming session)
      // We can detect this by checking if the existing message is before the most recent user message
      const lastUserMessageIndex = arrayFindLastIndex(prev, msg => msg.role === 'user')
      const existingToolMessage = prev.find((msg, index) => 
        msg.role === 'tool_execution' && 
        msg.toolCalls?.some(tc => tc.id === normalized.id) &&
        // Only consider it a duplicate if it's from a previous conversation (before last user message)
        (lastUserMessageIndex === -1 || index < lastUserMessageIndex)
      )
      if (existingToolMessage) {
        // Tool call already exists in a previous conversation, don't add duplicate
        console.log('🔧 [KorvoChat] Tool message exists in previous conversation, skipping:', {
          toolCallId: normalized.id,
          existingMessageIndex: prev.indexOf(existingToolMessage),
          lastUserMessageIndex
        })
        return prev
      }
      
      const toolMessage: ChatMessage = {
        id: messageId,
        role: 'tool_execution',
        content: buildToolCallContent(normalized, 'start'),
        timestamp: Date.now(),
        toolCalls: [normalized]
      }

      toolMessageMapRef.current.set(normalized.id, messageId)
      
      // Find the assistant message that's currently being streamed (empty or has content)
      // Insert tool messages before it so they appear in real-time
      // Look for the most recent assistant message (likely the one being streamed)
      // Use the lastUserMessageIndex already calculated above
      let assistantIndex = -1
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === 'assistant') {
          assistantIndex = i
          break
        }
      }
      
      // If we have a user message and an assistant message, ensure tool message goes between them
      // or right before the assistant if it's the most recent message
      if (assistantIndex !== -1) {
        // Check if there are any tool messages already between the last user message and assistant
        // If so, insert after the last tool message in that range
        if (lastUserMessageIndex !== -1 && assistantIndex > lastUserMessageIndex) {
          // Look for the last tool message between user and assistant
          let lastToolIndex = -1
          for (let i = assistantIndex - 1; i > lastUserMessageIndex; i--) {
            if (prev[i].role === 'tool_execution') {
              lastToolIndex = i
              break
            }
          }
          
          if (lastToolIndex !== -1) {
            // Insert after the last tool message
            console.log('🔧 [KorvoChat] Adding tool message after existing tool message at index:', lastToolIndex + 1)
            return [
              ...prev.slice(0, lastToolIndex + 1),
              toolMessage,
              ...prev.slice(lastToolIndex + 1)
            ]
          }
        }
        
        // Insert before the assistant message
        console.log('🔧 [KorvoChat] Adding tool message before assistant message at index:', assistantIndex)
        return [
          ...prev.slice(0, assistantIndex),
          toolMessage,
          ...prev.slice(assistantIndex)
        ]
      }
      
      // If no assistant message found but we have a user message, insert after the last user message
      if (lastUserMessageIndex !== -1) {
        console.log('🔧 [KorvoChat] Adding tool message after last user message at index:', lastUserMessageIndex + 1)
        return [
          ...prev.slice(0, lastUserMessageIndex + 1),
          toolMessage,
          ...prev.slice(lastUserMessageIndex + 1)
        ]
      }
      
      // If no assistant or user message found, append normally
      console.log('🔧 [KorvoChat] Adding tool message at end (no assistant or user message found)')
      return [...prev, toolMessage]
    })
  }, [normalizeToolCall, buildToolCallContent])

  const applyToolCallUpdate = useCallback(
    (raw: unknown, options?: { progress?: number; message?: string }) => {
      const normalized = normalizeToolCall(raw)
      const messageId = toolMessageMapRef.current.get(normalized.id)

      console.log('🔧 [KorvoChat] applyToolCallUpdate called:', {
        toolCallId: normalized.id,
        toolName: normalized.name,
        messageId,
        hasResult: !!normalized.result,
        success: normalized.success,
        timestamp: Date.now(),
        progress: options?.progress,
        toolProgressMessage: options?.message,
      })

      if (!messageId) {
        // If message doesn't exist in map, try to find it in the messages array
        // This can happen if tool_call_update arrives before tool_call_message is fully processed
        console.log('🔧 [KorvoChat] Tool message not found in map, searching messages array for toolCallId:', normalized.id)
        
        // Use a closure to access current messages state
        setMessages(prev => {
          const existingMessage = prev.find(msg => 
            msg.role === 'tool_execution' && 
            msg.toolCalls?.some(tc => tc.id === normalized.id)
          )
          
          if (existingMessage) {
            // Found it! Update the map and then update the message
            console.log('🔧 [KorvoChat] Found existing tool message, updating map and message:', existingMessage.id)
            toolMessageMapRef.current.set(normalized.id, existingMessage.id)
            
            const hasFinalFailure =
              (typeof normalized.error === 'string' && normalized.error.trim() !== '') ||
              normalized.success === false
            const nextContent = hasFinalFailure
              ? buildToolCallContent(normalized, 'update')
              : existingMessage.content || buildToolCallContent(normalized, 'start')
            return prev.map(msg =>
              msg.id === existingMessage.id
                ? {
                    ...msg,
                    content: nextContent,
                    toolCalls: [normalized] // Update toolCalls with latest data (including result)
                  }
                : msg
            )
          } else {
            // Message doesn't exist, create it
            console.log('🔧 [KorvoChat] Tool message not found anywhere, creating new one')
            // We can't call appendToolCallMessage here (it's async), so return unchanged
            // The appendToolCallMessage will be called below
            return prev
          }
        })
        
        // If we didn't find it in the state, create it
        if (!toolMessageMapRef.current.get(normalized.id)) {
          appendToolCallMessage(raw)
        }
        return
      }

      // Keep content as "Executing tool: ..." format, don't change it to "Tool result: ..."
      // The result will be shown in the expandable details section
      console.log('🔧 [KorvoChat] Updating tool message:', messageId, 'with toolCall update (keeping original content)')

      setMessages(prev => {
        // Find and update the tool message, preserving its position
        const msgIndex = prev.findIndex(msg => msg.id === messageId)
        if (msgIndex === -1) {
          // Message not found, create it
          appendToolCallMessage(raw)
          return prev
        }
        
        // Get the existing message to preserve its content
        const existingMessage = prev.find(msg => msg.id === messageId)
        const hasFinalFailure =
          (typeof normalized.error === 'string' && normalized.error.trim() !== '') ||
          normalized.success === false
        const nextContent = hasFinalFailure
          ? buildToolCallContent(normalized, 'update')
          : existingMessage?.content || buildToolCallContent(normalized, 'start')
        
        // Update the message in place; on failure, surface `Tool <name> failed: …` in the header (US-1.3 / strict tests)
        return prev.map(msg =>
          msg.id === messageId
            ? {
                ...msg,
                content: nextContent,
                toolCalls: [normalized] // Update toolCalls with latest data (including result)
              }
            : msg
        )
      })
    },
    [appendToolCallMessage, normalizeToolCall, buildToolCallContent]
  )

  const createLocalSession = useCallback((preferredId?: string | null): ChatSession => ({
    id: preferredId ?? `korvochat-${Date.now()}`,
    title: 'KorvoChat Session',
    threadId: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    metadata: {
      packageName: config.portal.packageName,
      systemName: config.portal.systemName,
      systemPrompt: config.agent.systemPrompt
    }
  }), [config.agent.systemPrompt, config.portal.packageName, config.portal.systemName])

  const persistSession = useCallback((nextSession: ChatSession | null) => {
    if (!isSessionControlled) {
      setInternalSession(nextSession)
    }
    if (nextSession) {
      onSessionChange?.(nextSession)
    }
  }, [isSessionControlled, onSessionChange])

  const saveStreamingSessionToStorage = useCallback((sessionToSave: StreamingSession | null) => {
    streamingSessionRef.current = sessionToSave
    const threadId = sessionToSave?.threadId ?? currentSession?.threadId
    const key = getStreamingSessionStorageKey(threadId)
    if (!key) return
    if (!sessionToSave) {
      try {
        localStorage.removeItem(key)
      } catch {
        // Ignore storage errors
      }
      return
    }
    try {
      localStorage.setItem(key, JSON.stringify(sessionToSave))
    } catch {
      // Ignore storage errors
    }
  }, [currentSession?.threadId, getStreamingSessionStorageKey])

  const loadStreamingSessionFromStorage = useCallback((): StreamingSession | null => {
    const threadId = currentSession?.threadId
    const key = getStreamingSessionStorageKey(threadId)
    if (!key) return null
    try {
      const stored = localStorage.getItem(key)
      return stored ? (JSON.parse(stored) as StreamingSession) : null
    } catch {
      return null
    }
  }, [currentSession?.threadId, getStreamingSessionStorageKey])

  const clearStoredConversation = useCallback(() => {
    if (!isSessionControlled) {
      setInternalSession(null)
    }
    setMessages([])
    toolMessageMapRef.current.clear()
    setIsTyping(false)
    setIsSending(false)
    setIsThreadLoading(false)
    setIsStreaming(false)
    isStreamingRef.current = false
  }, [isSessionControlled])

  const cancelActiveStreaming = useCallback(() => {
    streamCancelledRef.current = true
    streamReaderRef.current?.cancel?.()
    streamReaderRef.current = null
    if (eventsSourceRef.current) {
      eventsSourceRef.current.close()
      eventsSourceRef.current = null
    }
    saveStreamingSessionToStorage(null)
    setIsStreaming(false)
    isStreamingRef.current = false
    setIsTyping(false)
    setIsSending(false)
  }, [saveStreamingSessionToStorage])

  const previousThreadIdRef = useRef<string | null>(currentSession?.threadId ?? null)

  useEffect(() => {
    const previousThreadId = previousThreadIdRef.current
    const nextThreadId = currentSession?.threadId ?? null
    if (previousThreadId && previousThreadId !== nextThreadId) {
      cancelActiveStreaming()
    }
    previousThreadIdRef.current = nextThreadId
  }, [currentSession?.threadId, cancelActiveStreaming])

  // localStorage functions removed - session and messages must be managed externally

  const portalApiBase = useMemo(() => getPortalApiBase(apiEndpoint), [apiEndpoint])
  
  // Internal consistency check - portal API base must be configured
  invariant(portalApiBase, 'Portal API base must be configured')

  const loadThreadHistory = useCallback(
    async (
      threadId: string,
      _sessionId?: string | null,
      includeDebug?: boolean,
      options?: { blockComposerWhileLoading?: boolean },
    ) => {
    const blockComposer = options?.blockComposerWhileLoading !== false
    const trimmedThreadId = threadId.trim()
    if (trimmedThreadId === '') {
      setMessages([])
      return
    }
    if (blockComposer) {
      setIsThreadLoading(true)
    }
    try {
      toolMessageMapRef.current.clear()
      
      // Use ThreadsClient.getMessages(); includeDebug (admin+debug) returns unredacted <%...%> content
      let rawData: unknown
      try {
        rawData = await threadsClient.getMessages(trimmedThreadId, 100, { debug: includeDebug ?? false })
      } catch (error) {
        if (isThreadHistoryNotFoundError(error)) {
          console.warn('[KorvoChat] thread history not found (404), starting empty:', trimmedThreadId)
          setMessages([])
          onThreadHistoryNotFound?.(trimmedThreadId)
          return
        }
        const entry = getErrorIdEntry(ERR_EDITOR_THREADS_LOAD)
        const userMessage = entry?.userMessage ?? 'Failed to load thread messages.'
        const err = new KorvoError(ERR_EDITOR_THREADS_LOAD, 1, { message: `${userMessage} ${error instanceof Error ? error.message : String(error)}` })
        console.error('[KorvoChat]', ERR_EDITOR_THREADS_LOAD, userMessage, error)
        throw err
      }
      
      // Defensive check: ensure we have valid data
      if (!rawData) {
        console.warn('[KorvoChat] API returned null/undefined data, using empty array')
        setMessages([])
        return
      }

      const payloadFromMessages =
        Array.isArray(rawData)
          ? rawData
          : rawData &&
            typeof rawData === 'object' &&
            Array.isArray((rawData as Record<string, unknown>).messages)
            ? (rawData as Record<string, unknown>).messages
            : null

      if (!payloadFromMessages) {
        console.error('[KorvoChat] API returned non-array data:', typeof rawData, rawData)
        setMessages([])
        return
      }

      const data = payloadFromMessages as Array<{ 
        id?: string
        role?: string
        content?: string
        timestamp?: number
        toolCalls?: unknown[]
        toolResults?: Array<{ toolCallId: string; name: string; result: unknown; success?: boolean; error?: string }>
        tokensIn?: number
        tokensOut?: number
        costCents?: number
        processingTime?: number
        createdAt?: string
        updatedAt?: string
        isQueryMessage?: boolean
        isHelperMessage?: boolean
        ragResults?: Array<{ content: string; title?: string; similarity?: number; sourceId?: string | number }>
        metadata?: Record<string, unknown>
      }>
      
      // Convert API response to RawMessage format for normalization
      const rawMessages: RawMessage[] = data.map((msg: Record<string, unknown>, index: number) => ({
        id: (msg.id as string) || `thread-${threadId}-${index}`,
        role: (msg.role as string) || 'assistant',
        content: (msg.content as string | null) ?? (msg.contentSoFar as string | null) ?? null,
        contentSoFar: (msg.contentSoFar as string | null) ?? null,
        timestamp: typeof msg.timestamp === 'number' ? msg.timestamp : undefined,
        createdAt: msg.createdAt != null ? new Date(msg.createdAt as string | number | Date) : new Date(),
        updatedAt: msg.updatedAt != null ? new Date(msg.updatedAt as string | number | Date) : null,
        toolCalls: msg.toolCalls ? (msg.toolCalls as unknown[]) : null,
        toolResults: msg.toolResults ? (msg.toolResults as unknown[]) : null,
        tokensIn: typeof msg.tokensIn === 'number' ? msg.tokensIn : null,
        tokensOut: typeof msg.tokensOut === 'number' ? msg.tokensOut : null,
        costCents: typeof msg.costCents === 'number' ? msg.costCents : null,
        processingTime: typeof msg.processingTime === 'number' ? msg.processingTime : null,
        isQueryMessage: Boolean(msg.isQueryMessage),
        isHelperMessage: Boolean(msg.isHelperMessage),
        ragResults: msg.ragResults as RawMessage['ragResults'],
        metadata: msg.metadata as Record<string, unknown> | undefined,
        files: undefined,
      })) as RawMessage[]
      
      // Normalize messages using MessageNormalizer
      // Explicitly preserve agent messages to ensure query/helper flags are set
      const normalized = MessageNormalizer.normalize(rawMessages)
      
      // Convert NormalizedMessage to ChatMessage format with extended fields
      const normalizedMessages: (ChatMessage & { 
        tokensIn?: number
        tokensOut?: number
        costCents?: number
        processingTime?: number
        createdAt?: string
        updatedAt?: string
        isQueryMessage?: boolean
        isHelperMessage?: boolean
        ragResults?: Array<{ content: string; title?: string; similarity?: number; sourceId?: string | number }>
        contentSoFar?: string | null
      })[] = normalized.map((msg) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        toolCalls: msg.toolCalls,
        toolResults: (msg.toolResults ?? []) as ToolResult[],
        files: msg.files,
        metadata: msg.metadata,
        tokensIn: msg.tokensIn,
        tokensOut: msg.tokensOut,
        costCents: msg.costCents,
        processingTime: msg.processingTime,
        createdAt: msg.createdAt,
        updatedAt: msg.updatedAt,
        isQueryMessage: msg.isQueryMessage ?? (msg.role === 'query'),
        isHelperMessage: msg.isHelperMessage ?? (msg.role === 'helper'),
        ...(msg.ragResults && { ragResults: msg.ragResults }),
      }))
      const deduped = normalizedMessages.reduce<ChatMessage[]>((acc, message) => {
        // Skip deduplication for messages with empty content (might be incomplete)
        if (!message.content || message.content.trim() === '') {
          // Only skip if we already have a message with the same ID (true duplicate)
          const existingById = acc.find(m => m.id === message.id);
          if (existingById) {
            return acc; // Skip duplicate by ID
          }
          // Otherwise, keep it (might be incomplete streaming message)
          acc.push(message);
          return acc;
        }
        
        // Deduplicate assistant messages (same content, even if timestamps are far apart)
        // This handles cases where follow-up calls create duplicate messages
        if (message.role === 'assistant' && acc.length > 0) {
          const last = acc[acc.length - 1]
          const lastTs = last.timestamp ?? 0
          const currentTs = message.timestamp ?? 0
          const ASSISTANT_DUPLICATE_WINDOW_MS = 10000 // Increased to 10 seconds

          // Check if same content (exact match or very similar)
          const isSameContent = last.content === message.content || 
            (last.content && message.content && 
             last.content.trim() === message.content.trim() &&
             Math.abs(last.content.length - message.content.length) < 10)

          if (last.role === 'assistant' && isSameContent) {
            // If timestamps are close, replace (might be streaming update)
            if (Math.abs(currentTs - lastTs) <= ASSISTANT_DUPLICATE_WINDOW_MS) {
              acc[acc.length - 1] = message
              return acc
            }
            // If timestamps are far apart but content is identical, keep the first one (skip duplicate)
            return acc
          }
        }
        
        // Deduplicate tool_execution messages (same tool call ID)
        if (message.role === 'tool_execution' && message.toolCalls && message.toolCalls.length > 0) {
          const toolCallId = message.toolCalls[0].id
          const existingToolMessage = acc.find(m => 
            m.role === 'tool_execution' && 
            m.toolCalls?.some(tc => tc.id === toolCallId)
          )
          
          if (existingToolMessage) {
            // Keep the one with more complete data (has result or later timestamp)
            const existingHasResult = existingToolMessage.toolCalls?.some(tc => tc.result && tc.result.trim() !== '')
            const currentHasResult = message.toolCalls?.some(tc => tc.result && tc.result.trim() !== '')
            
            if (currentHasResult && !existingHasResult) {
              // Replace with message that has result
              const index = acc.indexOf(existingToolMessage)
              acc[index] = message
            }
            // Otherwise keep existing, skip duplicate
            return acc
          }
        }
        
        acc.push(message)
        return acc
      }, [])

      // Agent messages (query and helper) are now included in the main GET endpoint response
      // No need to fetch from debug endpoint - they're already in the normalized messages
      // Sort by timestamp to ensure correct order (user → query → helper → assistant)
      const sortedMessages = deduped.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
      
      console.log('[KorvoChat] Loaded messages:', {
        total: sortedMessages.length,
        byRole: sortedMessages.reduce((acc, m) => {
          acc[m.role] = (acc[m.role] || 0) + 1
          return acc
        }, {} as Record<string, number>),
        hasQueryMessages: sortedMessages.some((m) => {
          if (m.role === 'query') return true
          const flags = m as ChatMessage & { isQueryMessage?: boolean }
          return flags.isQueryMessage === true
        }),
        hasHelperMessages: sortedMessages.some((m) => {
          if (m.role === 'helper') return true
          const flags = m as ChatMessage & { isHelperMessage?: boolean }
          return flags.isHelperMessage === true
        }),
      })
      
      if (sortedMessages.length === 0) {
        setMessages(prev => mergeThreadMessagesPreservingClientErrors([], prev))
        return
      }
      setMessages(prev => mergeThreadMessagesPreservingClientErrors(sortedMessages, prev))
    } catch (error) {
      const entry = getErrorIdEntry(ERR_EDITOR_THREADS_LOAD)
      const userMessage = entry?.userMessage ?? 'Failed to load thread messages.'
      const err = error instanceof KorvoError ? error : new KorvoError(ERR_EDITOR_THREADS_LOAD, 2, { message: `${userMessage} ${error instanceof Error ? error.message : String(error)}` })
      console.error('[KorvoChat]', err.id, userMessage, error)
      // Don't clear messages on error - keep existing messages if any
      // Only set empty array if we don't have any messages yet
      setMessages(prev => prev.length > 0 ? prev : [])
      onError?.(err)
    } finally {
      if (blockComposer) {
        setIsThreadLoading(false)
        setIsTyping(false)
      }
    }
  }, [threadsClient, onError, onThreadHistoryNotFound])

  const schedulePostStreamHydration = useCallback(
    (threadId: string, sessionId?: string | null) => {
      const key = `${threadId}:${sessionId ?? 'null'}`
      if (typeof window === 'undefined') return
      if (postStreamHydrationTimersRef.current.has(key)) return

      let attempts = 0
      const maxAttempts = 5
      const intervalMs = 2000

      const timerId = window.setInterval(() => {
        attempts += 1
        void loadThreadHistory(threadId, sessionId, isAdmin && showDebug && config.features.debug, {
          blockComposerWhileLoading: false,
        }).catch(() => {
          // loadThreadHistory already logs its own errors
        })
        if (attempts >= maxAttempts) {
          window.clearInterval(timerId)
          postStreamHydrationTimersRef.current.delete(key)
        }
      }, intervalMs)

      postStreamHydrationTimersRef.current.set(key, timerId)
    },
    [loadThreadHistory, isAdmin, showDebug, config.features.debug],
  )

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement> | React.FormEvent<HTMLTextAreaElement>) => {
    // Prevent input changes when disabled (defensive check using ref for immediate access)
    if (isSending || isThreadLoading || isStreaming || isStreamingRef.current || isOffline || !isThreadReady) {
      event.preventDefault()
      event.stopPropagation()
      return
    }
    const incoming = event.currentTarget.value
    if (maxMessageLength > 0 && incoming.length > maxMessageLength) {
      const truncated = incoming.slice(0, maxMessageLength)
      setIsInputOverLimit(false) // Reset limit flag after truncation
      setInput(truncated)
      return
    }
    setIsInputOverLimit(false)
    setInput(incoming)
  }, [maxMessageLength, isSending, isThreadLoading, isStreaming, isOffline, isThreadReady])
  
  // Handle input events (fires for programmatic changes like fill())
  const handleInput = useCallback((event: React.FormEvent<HTMLTextAreaElement>) => {
    // Sync React state with DOM value when input event fires
    // This handles cases where fill() or other programmatic changes bypass onChange
    handleInputChange(event)
  }, [handleInputChange])
  
  // Sync React state with DOM value when they differ (handles programmatic changes like fill())
  // Use a ref to track the last synced value to avoid infinite loops
  const lastSyncedValueRef = useRef<string>('')
  useEffect(() => {
    if (!inputRef.current) return
    
    const textarea = inputRef.current
    const domValue = textarea.value
    const reactValue = input
    
    // If DOM value differs from React state AND from last synced value
    // (e.g., due to maxLength truncation from fill())
    if (domValue !== reactValue && domValue !== lastSyncedValueRef.current) {
      // Only sync if component is not disabled (mirror composer + send enable preconditions)
      if (
        isThreadReady &&
        !isSending &&
        !isThreadLoading &&
        !isStreaming &&
        !isStreamingRef.current &&
        !isOffline
      ) {
        const incoming = domValue
        if (maxMessageLength > 0 && incoming.length > maxMessageLength) {
          const truncated = incoming.slice(0, maxMessageLength)
          setIsInputOverLimit(false)
          setInput(truncated)
          lastSyncedValueRef.current = truncated
        } else {
          setIsInputOverLimit(false)
          setInput(incoming)
          lastSyncedValueRef.current = incoming
        }
      }
    } else if (domValue === reactValue) {
      // Update last synced value when React state matches DOM
      lastSyncedValueRef.current = reactValue
    }
  }, [input, maxMessageLength, isSending, isThreadLoading, isStreaming, isOffline, isThreadReady])

  useEffect(() => {
    const handleSessionDeleted = () => {
      clearStoredConversation()
    }

    const handleClearConversation = () => {
      clearStoredConversation()
    }

    window.addEventListener('chatSessionDeleted', handleSessionDeleted)
    window.addEventListener('chat:clear-conversation', handleClearConversation)
    return () => {
      window.removeEventListener('chatSessionDeleted', handleSessionDeleted)
      window.removeEventListener('chat:clear-conversation', handleClearConversation)
    }
  }, [clearStoredConversation])

  // Initialize session with persistence for uncontrolled usage
  // Use stable dependencies (id, threadId) instead of entire initialSession object to prevent infinite loops
  // Use validatedInitialSession (from Zod parsing) instead of raw initialSession
  const initialSessionId = validatedInitialSession?.id ?? null
  const initialSessionThreadId = validatedInitialSession?.threadId ?? null
  
  useEffect(() => {
    if (isSessionControlled) return
    
    // Prevent re-initialization if session ID and threadId haven't changed
    // This fixes infinite loop when initialSession object is recreated on every render
    // Re-init when threadId arrives (e.g. MentorChatClient: null -> threadId after POST)
    if (lastInitializedSessionIdRef.current !== null && lastInitializedSessionIdRef.current === initialSessionId && lastInitializedSessionThreadIdRef.current === initialSessionThreadId) {
      return // Already initialized with this session ID and threadId
    }
    
    lastInitializedSessionIdRef.current = initialSessionId
    lastInitializedSessionThreadIdRef.current = initialSessionThreadId
    
    // If validatedInitialSession is provided, use it
    if (validatedInitialSession && initialSessionId) {
      setInternalSession(validatedInitialSession)
      if (visibleInitialSessionMessages.length > 0) {
        setMessages(initialSessionInitialMessages)
      }
      if (initialSessionThreadId) {
        void loadThreadHistory(initialSessionThreadId, initialSessionId, isAdmin && showDebug && config.features.debug)
      }
      return
    }
    
    // No initialSession provided - create new local session
    const createdSession = createLocalSession()
    setInternalSession(createdSession)
    lastInitializedSessionIdRef.current = createdSession.id
    lastInitializedSessionThreadIdRef.current = createdSession.threadId ?? null
  }, [
    isSessionControlled,
    createLocalSession,
    initialSessionId,
    initialSessionThreadId,
    validatedInitialSession,
    loadThreadHistory,
    threadsClient,
    initialSessionInitialMessages,
    visibleInitialSessionMessages,
    isAdmin,
    showDebug,
    config.features.debug,
  ])

  // Load messages whenever an external session changes
  useEffect(() => {
    if (!isSessionControlled) return
    toolMessageMapRef.current.clear()
    setMessages([])
    if (controlledSessionInitialMessages.length > 0) {
      setMessages(controlledSessionInitialMessages)
    }
    // Reset all loading/streaming states when session changes
    setIsThreadLoading(false)
    setIsTyping(false)
    setIsSending(false)
    setIsStreaming(false)
    isStreamingRef.current = false
    if (!externalSessionId) {
      return
    }

    if (externalThreadId) {
      setIsThreadLoading(true)
      void loadThreadHistory(externalThreadId, externalSessionId, isAdmin && showDebug && config.features.debug)
    }
  }, [
    isSessionControlled,
    externalSessionId,
    externalThreadId,
    loadThreadHistory,
    threadsClient,
    controlledSessionInitialMessages,
    isAdmin,
    showDebug,
    config.features.debug,
  ])

  // Send initialHiddenMessage once per thread when thread is empty (e.g. mentor session start).
  // Wait for thread history to load first so we don't send on reload of existing thread.
  // Use sessionStorage to survive React Strict Mode remounts (ref resets on unmount).
  useEffect(() => {
    const threadId = currentSession?.threadId ?? null
    if (
      !initialHiddenMessage?.trim() ||
      !threadId ||
      isThreadLoading ||
      messages.length > 0 ||
      isSessionControlled
    ) {
      return
    }
    const storageKey = `korvochat-initial-sent-${config.portal.packageName}-${config.portal.systemName}-${threadId}`
    if (lastSentInitialHiddenForThreadIdRef.current === threadId) return
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey) === '1') return
    } catch {
      /* sessionStorage unavailable */
    }
    lastSentInitialHiddenForThreadIdRef.current = threadId
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(storageKey, '1')
    } catch {
      /* sessionStorage unavailable */
    }
    const msg = initialHiddenMessage.trim()
    const timeoutId = setTimeout(() => {
      void handleSendMessageRef.current(msg)
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [
    currentSession?.threadId,
    initialHiddenMessage,
    isThreadLoading,
    messages.length,
    isSessionControlled,
    config.portal.packageName,
    config.portal.systemName,
  ])

  // When admin turns on debug, refetch so <%...%> content is returned (only on transition to true)
  const prevShowDebugRef = useRef(false)
  useEffect(() => {
    const justEnabled = showDebug && !prevShowDebugRef.current
    prevShowDebugRef.current = showDebug
    if (!justEnabled || !isAdmin || !config.features.debug || !currentSession?.threadId) return
    void loadThreadHistory(currentSession.threadId, currentSession.id, true)
  }, [showDebug, isAdmin, config.features.debug, currentSession?.threadId, currentSession?.id, loadThreadHistory])

  // When hideComposer, register handleSendMessage so parent (e.g. shell slot composer) can send.
  useEffect(() => {
    if (!hideComposer || !onRegisterSend) return
    onRegisterSend((content: string) => {
      void handleSendMessageRef.current(content)
    })
    return () => {
      onRegisterSend(null)
    }
  }, [hideComposer, onRegisterSend])

  // localStorage persistence removed - session and messages must be managed externally

  // Best-effort resumable streaming on mount / session change.
  // If we find a recent in-progress StreamingSession for this thread, we attempt
  // a lightweight resume call. If it fails or immediately completes, we simply
  // fall back to the normal history hydration without surfacing any error.
  useEffect(() => {
    const threadId = currentSession?.threadId
    const sessionId = currentSession?.id ?? null
    if (!threadId) return

    const existing = loadStreamingSessionFromStorage()
    if (!existing || existing.status !== 'in_progress') return

    const THREE_MINUTES_MS = 3 * 60 * 1000
    if (Date.now() - existing.updatedAt > THREE_MINUTES_MS) {
      // Expired resume window; clear stale state
      saveStreamingSessionToStorage(null)
      return
    }

    // Attempt a best-effort resume only when unified endpoint is active
    const streamingDisabled = config.features.streaming === false
    const useUnifiedEndpoint = config.portal.streamingEndpoint === undefined && !streamingDisabled
    if (!useUnifiedEndpoint) return

    const controller = new AbortController()

    void (async () => {
      try {
        // Resume streaming - use ThreadsClient.sendMessage() with resume flag
        // Note: ThreadsClient doesn't support resume flag directly; fetch is used until
        // ThreadsClient gains a dedicated resume API.
        const response = await fetch(`${portalApiBase}/threads/${threadId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role: 'user',
            content: '',
            clientMessageId: existing.clientMessageId,
            resume: true,
            lastEventId: existing.lastEventId,
          }),
          signal: controller.signal,
        })

        if (!response.ok || !response.body) {
          // Resume failed; clear state and immediately hydrate from history
          saveStreamingSessionToStorage(null)
          try {
            await loadThreadHistory(threadId, sessionId, isAdmin && showDebug && config.features.debug)
          } catch {
            // History hydration errors are already logged inside loadThreadHistory
          }
          return
        }

        // Set streaming state immediately when resume stream starts
        isStreamingRef.current = true
        setIsStreaming(true)
        setIsSending(true)

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let lastEventIdFromStream: string | null = null
        let hasReceivedContent = false
        let resumeStreamTerminalPending = false

        // Yield once so UI disables before replaying the stream
        await new Promise(resolve => setTimeout(resolve, 0))

        while (true) {
           
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          { const popped = lines.pop(); buffer = popped !== undefined ? popped : ''; }

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            let data: unknown
            try {
              const ssePayload = line.slice(6).trim()
              data = JSON.parse(ssePayload)
            } catch {
              continue
            }

            const typed = data as { type?: string; eventId?: unknown; content?: unknown }
            
            // Process content events the same way as main stream
            if (typed.type === 'content' && typed.content) {
              hasReceivedContent = true
              setMessages((prev) => {
                let messages = [...prev] // Create a new array to ensure React detects the change

                // Find the last assistant message; if none exists yet, create one.
                let index = -1
                for (let i = messages.length - 1; i >= 0; i -= 1) {
                  if (messages[i].role === 'assistant') {
                    index = i
                    break
                  }
                }

                if (index === -1) {
                  const assistantMessage: ChatMessage = {
                    id: `msg-${Date.now()}`,
                    role: 'assistant',
                    content: '',
                    timestamp: Date.now(),
                  }
                  messages = [...messages, assistantMessage]
                  index = messages.length - 1
                }

                const target = messages[index]
                const updated = {
                  ...target,
                  content: (target.content !== undefined && target.content !== null && target.content !== '' ? target.content : '') + String(typed.content),
                }
                const next = [...messages] // Create new array
                next[index] = updated
                return next
              })
            } else if (typed.type === 'client_tool_call' || typed.type === 'client_tool_instruction') {
              await executeClientToolRef.current(data as ClientToolEventPayload)
            } else if (typed.type === 'done' || typed.type === 'error') {
              // Server indicates either completion or non-resumable stream.
              if (typed.type === 'done') {
                // Stream completed - reset state and hydrate from history
                setIsStreaming(false)
                isStreamingRef.current = false
                setIsTyping(false)
                setIsSending(false)
              } else {
                // Error occurred - reset state
                setIsStreaming(false)
                isStreamingRef.current = false
                setIsTyping(false)
                setIsSending(false)
              }
              saveStreamingSessionToStorage(null)
              // Defer hydration until the HTTP body is fully read — `done` may arrive in an earlier
              // chunk than `client_tool_instruction` (same ordering as handleSendMessage).
              resumeStreamTerminalPending = true
              continue
            }

            // Update lastEventId if server chose to emit any resumable events
            if (typed.eventId) {
              lastEventIdFromStream = String(typed.eventId)
              const nextSession: StreamingSession = {
                ...existing,
                lastEventId: lastEventIdFromStream,
                updatedAt: Date.now(),
                status: 'in_progress',
                ownerTabId: tabIdRef.current,
              }
              saveStreamingSessionToStorage(nextSession)
            }
          }

          // Yield between chunks so timers/rendering can progress during resume replay
           
          await new Promise(resolve => setTimeout(resolve, 0))
        }

        if (resumeStreamTerminalPending) {
          try {
            await loadThreadHistory(threadId, sessionId, isAdmin && showDebug && config.features.debug)
          } catch {
            // Swallow; loadThreadHistory logs its own failures
          }
        }

        // After resume replay completes, attach to events stream for live updates
        // Only if we received content (stream is still active)
        if (hasReceivedContent && lastEventIdFromStream) {
          attachEventsStreamRef.current(threadId, lastEventIdFromStream)
        } else {
          // No content received or stream completed - reset state
          setIsStreaming(false)
          isStreamingRef.current = false
          setIsTyping(false)
          setIsSending(false)
        }
      } catch {
        // Network / cancellation – clear state, then hydrate from history
        saveStreamingSessionToStorage(null)
        try {
          await loadThreadHistory(threadId, sessionId, isAdmin && showDebug && config.features.debug)
        } catch {
          // Already logged inside loadThreadHistory
        }
        setIsSending(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [
    currentSession?.threadId,
    currentSession?.id,
    config.features.streaming,
    config.features.debug,
    config.portal.streamingEndpoint,
    loadStreamingSessionFromStorage,
    loadThreadHistory,
    portalApiBase,
    saveStreamingSessionToStorage,
    isAdmin,
    showDebug,
  ])

  // Cleanup any post-stream hydration timers and SSE source on unmount
  useEffect(() => {
    const timersMap = postStreamHydrationTimersRef.current
    return () => {
      if (typeof window === 'undefined') return
      for (const timerId of timersMap.values()) {
        window.clearInterval(timerId)
      }
      timersMap.clear()
      if (eventsSourceRef.current) {
        eventsSourceRef.current.close()
        eventsSourceRef.current = null
      }
    }
  }, [])

  // Scroll to bottom function
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    // Ensure behavior is a valid string, not an object
    const behaviorStr = typeof behavior === 'string' ? behavior : 'smooth'
    
    const container = messagesContainerRef.current
    if (container) {
      // Use direct container scroll for more reliable behavior during rapid updates
      if (behaviorStr === 'auto') {
        container.scrollTop = container.scrollHeight
      } else {
        // For smooth scrolling, prefer scrollIntoView; jsdom test environment may
        // not implement Element.scrollTo, so guard it explicitly.
        const endEl = messagesEndRef.current
        if (endEl && typeof endEl.scrollIntoView === 'function') {
          endEl.scrollIntoView({ behavior: behaviorStr })
        } else if (typeof container.scrollTo === 'function') {
          container.scrollTo({ top: container.scrollHeight, behavior: behaviorStr })
        } else {
          // Minimal fallback for non-browser/test environments
          container.scrollTop = container.scrollHeight
        }
      }
    } else {
      // Fallback to messagesEndRef if container not available
      const endEl = messagesEndRef.current
      if (endEl && typeof endEl.scrollIntoView === 'function') {
        endEl.scrollIntoView({ behavior: behaviorStr })
      }
    }
  }

  // Handle scroll detection
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return
    
    const container = messagesContainerRef.current
    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    
    const BOTTOM_THRESHOLD_PX = 100
    const atBottom = distanceFromBottom <= BOTTOM_THRESHOLD_PX
    
    // Update refs and state
    isAtBottomRef.current = atBottom
    setShowScrollToBottom(!atBottom)
    
    // Detect manual scrolling
    setIsUserScrolling(true)
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false)
    }, 150)
  }, [])

  // Auto-scroll when new messages are added (only if user is at bottom and not actively scrolling)
  useEffect(() => {
    const messageCountChanged = messages.length !== lastMessageCountRef.current
    lastMessageCountRef.current = messages.length

    if (messageCountChanged && messages.length > 0 && isAtBottomRef.current && !isUserScrolling) {
      setTimeout(() => {
        scrollToBottom('smooth')
      }, 100)
    }
  }, [messages.length, isUserScrolling])

  // Auto-scroll during streaming when content updates (only if user is at bottom and not actively scrolling)
  const lastAssistantContentLengthRef = useRef(0)
  const scrollAnimationFrameRef = useRef<number | null>(null)
  const lastScrollTimeRef = useRef(0)
  const THROTTLE_MS = 50 // Throttle scrolling to max once per 50ms for smoothness
  
  // Track last messages array reference to detect content changes
  const lastMessagesRef = useRef<ChatMessage[]>([])
  useEffect(() => {
    if (isStreaming && messages.length > 0) {
      // Find the last assistant message
      const lastAssistant = messages.filter(m => m.role === 'assistant').pop()
      const currentLength = lastAssistant?.content?.length || 0
      
      // Check if messages array actually changed (reference or content)
      const messagesChanged = lastMessagesRef.current !== messages || 
        lastMessagesRef.current.length !== messages.length ||
        (lastAssistant && lastMessagesRef.current.find(m => m.id === lastAssistant.id)?.content !== lastAssistant.content)
      
      // Only scroll if content actually changed and user is at bottom
      // CRITICAL: Never scroll if user has scrolled up to read previous messages
      if (messagesChanged && currentLength !== lastAssistantContentLengthRef.current && isAtBottomRef.current && !isUserScrolling) {
        const now = Date.now()
        const timeSinceLastScroll = now - lastScrollTimeRef.current
        
        // Throttle scrolling to prevent jank during rapid updates
        if (timeSinceLastScroll >= THROTTLE_MS) {
          // Cancel any pending scroll animation
          if (scrollAnimationFrameRef.current !== null) {
            cancelAnimationFrame(scrollAnimationFrameRef.current)
          }
          // Use requestAnimationFrame for smooth scrolling during streaming
          scrollAnimationFrameRef.current = requestAnimationFrame(() => {
            const container = messagesContainerRef.current
            if (container && isAtBottomRef.current && !isUserScrolling) {
              // Direct container scroll for reliable behavior during rapid updates
              container.scrollTop = container.scrollHeight
              lastScrollTimeRef.current = Date.now()
            }
            scrollAnimationFrameRef.current = null
          })
        }
      }
      lastAssistantContentLengthRef.current = currentLength
      lastMessagesRef.current = messages
    } else {
      lastAssistantContentLengthRef.current = 0
      lastMessagesRef.current = []
      if (scrollAnimationFrameRef.current !== null) {
        cancelAnimationFrame(scrollAnimationFrameRef.current)
        scrollAnimationFrameRef.current = null
      }
    }
    
    return () => {
      if (scrollAnimationFrameRef.current !== null) {
        cancelAnimationFrame(scrollAnimationFrameRef.current)
        scrollAnimationFrameRef.current = null
      }
    }
  }, [messages, isStreaming, isUserScrolling])

  // Set up scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll)
    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [handleScroll])

  // Auto-focus input on mount and after sending messages
  useEffect(() => {
    if (!isSending) {
      focusComposer()
    }
  }, [focusComposer, isSending])

  const executeClientTool = useCallback(async (eventData: ClientToolEventPayload) => {
    const callId = typeof eventData.callId === 'string'
      ? eventData.callId
      : typeof eventData.requestId === 'string'
        ? eventData.requestId
        : null
    const toolName = typeof eventData.toolName === 'string'
      ? eventData.toolName
      : typeof eventData.toolId === 'string'
        ? eventData.toolId
        : null

    if (!callId || !toolName) {
      console.warn('Client tool event missing callId/toolName', eventData)
      return
    }

    if (clientToolCallsInFlight.current.has(callId)) {
      return
    }

    clientToolCallsInFlight.current.add(callId)

    const args =
      eventData.args && typeof eventData.args === 'object' && !Array.isArray(eventData.args)
        ? (eventData.args as Record<string, unknown>)
        : {}

    console.log('🔧 [KorvoChat] Executing client tool:', toolName, 'Available tools:', Object.keys(config.tools.clientTools))
    
    // Warn if get_client_time is missing (it should always be available)
    if (toolName === 'get_client_time' && !config.tools.clientTools['get_client_time']) {
      console.warn('🔧 [KorvoChat] WARNING: get_client_time is missing from clientTools. Ensure getBaseClientTools() is included in config.')
    }
    
    const handler = config.tools.clientTools[toolName]
    let resultPayload: ToolResultPayload

    if (!handler) {
      console.error('🔧 [KorvoChat] Client tool not found:', toolName, 'Available:', Object.keys(config.tools.clientTools))
      resultPayload = {
        success: false,
        error: `Client tool ${toolName} is not registered`
      }
    } else {
      console.log('🔧 [KorvoChat] Client tool handler found, executing...')
      try {
        const data = await Promise.resolve(handler(args))
        resultPayload = isToolResultPayload(data) ? data : { success: true, data }
      } catch (error) {
        const message = (error instanceof Error ? (error instanceof Error ? error.message : String(error)) : String(error))
        resultPayload = {
          success: false,
          error: message
        }
      }
    }

    try {
      console.log('🔧 [KorvoChat] Sending client tool response:', { callId, result: resultPayload })
      const response = await fetch(`${portalApiBase}/tools/client-response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          callId,
          result: resultPayload
        })
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('🔧 [KorvoChat] Client tool response failed:', response.status, errorText)
      } else {
        console.log('🔧 [KorvoChat] Client tool response sent successfully')
      }
    } catch (error) {
      console.error('🔧 [KorvoChat] Failed to send client tool response:', error)
    } finally {
      clientToolCallsInFlight.current.delete(callId)
    }
  }, [config.tools.clientTools, portalApiBase])
  executeClientToolRef.current = executeClientTool

  // Attach to shared SSE events stream for this thread. This is used both for
  // the "first" tab (live streaming) and for mid-stream reconnection after
  // reload/tab switch. The underlying Portal streaming layer ensures that
  // every event carries a monotonically increasing eventId so we can safely
  // request replay from a given point.
  const attachEventsStream = useCallback(
    (threadId: string, lastEventId: string | null) => {
      if (typeof window === 'undefined') return
      if (!threadId) return

      // Close any existing stream first
      if (eventsSourceRef.current) {
        eventsSourceRef.current.close()
        eventsSourceRef.current = null
      }

      try {
        const base = `${config.portal.apiEndpoint}/threads/${threadId}/events`
        const url = new URL(base, window.location.origin)
        if (lastEventId) {
          url.searchParams.set('lastEventId', lastEventId)
        }

        const es = new EventSource(url.toString())
        eventsSourceRef.current = es

        es.onmessage = async (event) => {
          let raw: unknown
          try {
            raw = JSON.parse(event.data)
          } catch {
            return
          }
          if (typeof raw !== 'object' || raw === null) {
            return
          }
          const data = raw as Record<string, unknown>

          if (data.type === 'client_tool_instruction' || data.type === 'client_tool_call') {
            await executeClientToolRef.current(data as ClientToolEventPayload)
            return
          }

          // Update resumable session eventId when present
          const evtId = data.eventId
          if (evtId && currentSession?.threadId) {
            const nextSession: StreamingSession = {
              threadId: currentSession.threadId,
              clientMessageId: streamingSessionRef.current?.clientMessageId !== null && streamingSessionRef.current?.clientMessageId !== undefined ? streamingSessionRef.current?.clientMessageId : '',
              lastEventId: String(evtId),
              updatedAt: Date.now(),
              status: 'in_progress',
              ownerTabId: tabIdRef.current,
            }
            saveStreamingSessionToStorage(nextSession)
          }

          // Handle content and tool events using the latest assistant message.
          setMessages((prev) => {
            let messages = prev

            // Find the last assistant message; if none exists yet, create one.
            let index = -1
            for (let i = messages.length - 1; i >= 0; i -= 1) {
              if (messages[i].role === 'assistant') {
                index = i
                break
              }
            }

            if (data.type === 'content' && data.content) {
              setIsTyping(false)
              if (!isStreamingRef.current) {
                isStreamingRef.current = true
                setIsStreaming(true)
              }
              setIsSending(true)
              if (!messages.length || index === -1) {
                const assistantMessage: ChatMessage = {
                  id: `msg-${Date.now()}`,
                  role: 'assistant',
                  content: '',
                  timestamp: Date.now(),
                }
                messages = [...messages, assistantMessage]
                index = messages.length - 1
              }

              const target = messages[index]
              const updated = {
                ...target,
                content: (target.content !== undefined && target.content !== null && target.content !== '' ? target.content : '') + String(data.content as string),
              }
              const next = messages.slice()
              next[index] = updated
              return next
            }

            if (data.type === 'tool_call_message' && data.toolCall) {
              appendToolCallMessage(data.toolCall)
            } else if (data.type === 'tool_call_update' && data.toolCall) {
              applyToolCallUpdate(data.toolCall)
            } else if (data.type === 'tool_call_result') {
              applyToolCallUpdate({
                id: data.callId as string,
                name: data.toolName as string,
                result: data.result,
                success: true,
              })
            } else if (data.type === 'tool_call_error') {
              applyToolCallUpdate({
                id: data.callId as string,
                name: data.toolName as string,
                error: data.error as string | undefined,
                success: false,
              })
            } else if (data.type === 'tool_call_progress') {
              applyToolCallUpdate(
                {
                  id: data.callId as string,
                  name: data.toolName as string,
                },
                { progress: data.progress as number | undefined, message: data.message as string | undefined },
              )
            } else if (data.type === 'tool_call_start') {
              appendToolCallMessage({
                id: data.callId as string,
                name: data.toolName as string,
                arguments: data.args,
              })
            } else if (data.type === 'error') {
              setIsStreaming(false)
              isStreamingRef.current = false
              setIsTyping(false)
              setIsSending(false)
              const streamErr = new KorvoError(ERR_CHAT_500, 6, {
                message:
                  typeof data.error === 'string' && data.error.length > 0 ? data.error : 'Streaming error',
                status: 500,
              })
              const errorLine = formatChatClientErrorMessage(streamErr, { isAdmin: isAdmin === true })
              const errorMsg: ChatMessage = {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: errorLine,
                timestamp: Date.now(),
                metadata: { error: true, fallback: true, korvoId: streamErr.id },
              }
              return [...messages, errorMsg]
            } else if (data.type === 'done') {
              setIsStreaming(false)
              isStreamingRef.current = false
              setIsTyping(false)
              setIsSending(false)
              saveStreamingSessionToStorage(null)
              // Empty response: replace assistant message with error
              const lastAssistant = arrayFindLast(messages, (m) => m.role === 'assistant')
              if (lastAssistant && !lastAssistant.content?.trim()) {
                const next = messages.slice()
                const idx = arrayFindLastIndex(messages, (m) => m.role === 'assistant')
                const emptyKorvo = new KorvoError(ERR_CHAT_500, CHAT_EMPTY_RESPONSE_THROW_ID, {
                  message: 'No response was received from the model.',
                  status: 500,
                })
                next[idx] = {
                  ...lastAssistant,
                  content: formatChatClientErrorMessage(emptyKorvo, { isAdmin: isAdmin === true }),
                  metadata: { ...lastAssistant.metadata, error: true, korvoId: emptyKorvo.id },
                }
                return next
              }
            }

            return messages
          })
        }

        es.onerror = () => {
          // Keep the connection; EventSource will retry automatically.
        }
      } catch (error) {
        console.warn('[KorvoChat] Failed to attach events stream', error)
      }
    },
    [
      config.portal.apiEndpoint,
      currentSession?.threadId,
      saveStreamingSessionToStorage,
      appendToolCallMessage,
      applyToolCallUpdate,
      setIsStreaming,
      setIsTyping,
      setIsSending,
    ],
  )
  attachEventsStreamRef.current = attachEventsStream

  // On mount / session change, attempt best-effort mid-stream reconnection by
  // polling thread history when we detect a recent in-progress StreamingSession
  // for this thread. This leverages persisted deltas so the assistant answer
  // continues to grow after reload, approximating streaming without requiring
  // long-lived process affinity on the server.
  useEffect(() => {
    const threadId = currentSession?.threadId
    if (!threadId) return

    const existing = loadStreamingSessionFromStorage()
    if (!existing || existing.status !== 'in_progress') return

    const THREE_MINUTES_MS = 3 * 60 * 1000
    if (Date.now() - existing.updatedAt > THREE_MINUTES_MS) {
      return
    }

    const streamingDisabled = config.features.streaming === false
    const useUnifiedEndpoint = config.portal.streamingEndpoint === undefined && !streamingDisabled
    if (!useUnifiedEndpoint) return

    schedulePostStreamHydration(threadId, currentSession?.id ?? null)
  }, [
    currentSession?.threadId,
    currentSession?.id,
    config.features.streaming,
    config.portal.streamingEndpoint,
    loadStreamingSessionFromStorage,
    schedulePostStreamHydration,
  ])

  // File handling functions
  const handleFileSelect = (files: FileList | null) => {
    if (!files || !config.features.fileUpload) return
    
    const selectedFiles = Array.from(files).filter(file => {
      // Basic file type validation
      const maxSize = 10 * 1024 * 1024 // 10MB
      return file.size <= maxSize
    })
    
    setAttachedFiles(prev => [...prev, ...selectedFiles])
  }

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (config.features.fileUpload) {
      setIsDragOver(true)
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (config.features.fileUpload) {
      handleFileSelect(e.dataTransfer.files)
    }
  }

  const handleStopStreaming = () => {
    if (!isStreaming) {
      return
    }

    streamCancelledRef.current = true
    setIsStreaming(false)
    isStreamingRef.current = false
    setIsTyping(false)
    setIsSending(false)

    try {
      void streamReaderRef.current?.cancel()
    } catch {
      // Best-effort cancel; ignore errors from reader cancellation
    }

    // Mark the current assistant message as incomplete/stopped
    if (lastAssistantMessageIdRef.current) {
      const stoppedId = lastAssistantMessageIdRef.current
      setMessages(prev =>
        prev.map(msg =>
          msg.id === stoppedId
            ? {
                ...msg,
                metadata: {
                  ...(msg.metadata || {}),
                  stopped: true,
                },
              }
            : msg,
        ),
      )
    }

    // Stopped answers are not resumable; clear any persisted streaming session
    if (streamingSessionRef.current) {
      saveStreamingSessionToStorage(null)
    }
  }

  const handleSendMessage = async (overrideContent?: string) => {
    const content = (overrideContent ?? input.trim()).trim()
    // Synchronous guard: prevent double send (button+Enter, Strict Mode remount)
    if (sendInProgressRef.current) return
    // Prevent sending while streaming or offline (check both state and ref for immediate access)
    if (
      isReadOnly ||
      (!content && attachedFiles.length === 0) ||
      isSending ||
      isStreaming ||
      isStreamingRef.current ||
      isOffline ||
      !isThreadReady
    )
      return

    sendInProgressRef.current = true
    // CRITICAL: Set streaming state synchronously before any async operations to disable input immediately
    flushSync(() => {
      isStreamingRef.current = true
      streamCancelledRef.current = false
      setIsStreaming(true)
      setIsSending(true)
      setIsTyping(true)
    })

    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content,
      timestamp: Date.now(),
      files: overrideContent ? undefined : attachedFiles.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size
      }))
    }

    const historyWithUser = [...messages, userMessage]
    setMessages(historyWithUser)
    if (!overrideContent) {
      setInput('')
      setAttachedFiles([])
    }

    let activeSession = currentSession
    if (!activeSession) {
      if (isSessionControlled) {
        sendInProgressRef.current = false
        setIsThreadLoading(false)
        setIsTyping(false)
        return
      }
      // Use initialSession.id if available, otherwise generate new ID
      activeSession = createLocalSession(initialSession?.id)
      persistSession(activeSession)
    }

    try {
      // Ensure we have a thread ID
      let threadId = activeSession.threadId
      if (!threadId) {
        const ensured = await ensureKorvoChatThreadId(activeSession, threadsClient, config, persistSession)
        threadId = ensured.threadId
        activeSession = ensured.activeSession
      }

      /**
       * Endpoint Pattern Selection
       * 
       * KorvoChat supports three endpoint patterns based on config.portal.streamingEndpoint:
       * 
       * 1. Unified Endpoint (DEFAULT - streamingEndpoint is undefined):
       *    - Single API call to /api/[module]/threads/[id]/messages
       *    - Endpoint handles both: persists user message + streams assistant response
       *    - Used by all Portal API modules (chat, editor, mentor)
       * 
       * 2. Custom Streaming Endpoint (streamingEndpoint is a string):
       *    - Step 1: Persist user message to /api/[module]/threads/[id]/messages
       *    - Step 2: Stream from custom endpoint (e.g., /custom/websocket-stream)
       *    - Use for packages with custom protocols (WebSocket, gRPC, etc.)
       * 
       * 3. Persistence Only (streamingEndpoint is null OR features.streaming === false):
       *    - Single API call to /api/[module]/threads/[id]/messages
       *    - Only persists user message, no streaming
       *    - Use for async processing, queues, background jobs
       * 
       * See KorvoChat.docs.md for detailed documentation and examples.
       */
      const streamingDisabled = config.features.streaming === false
      const useUnifiedEndpoint = config.portal.streamingEndpoint === undefined && !streamingDisabled
      const persistenceOnly = config.portal.streamingEndpoint === null || streamingDisabled

      let response: Response

      if (!threadId) {
        throw new KorvoError(ERR_BAD_REQUEST_400, 1, {
          message: 'Thread ID is required to send a message',
          status: 400,
        })
      }

      if (useUnifiedEndpoint) {
        // Unified endpoint: persist user message AND stream assistant response
        // This is the default for modules using Portal API (chat, editor, mentor)
        // Use ThreadsClient.sendMessage() which returns ReadableStream
        const stream = await threadsClient.sendMessage(threadId, {
          role: 'user',
          content: userMessage.content
        }, {
          clientMessageId: userMessage.id
        })
        // Convert ReadableStream to Response-like object for compatibility with existing stream parsing code
        response = {
          ok: true,
          status: 200,
          body: stream,
          headers: new Headers({ 'Content-Type': 'text/event-stream' }),
          json: async () => ({}),
          text: async () => '',
        } as Response
      } else if (persistenceOnly) {
        // Persistence only: save message, no streaming
        // Use ThreadsClient.sendMessage() but don't consume the stream
        try {
          await threadsClient.sendMessage(threadId, {
            role: 'user',
            content: userMessage.content
          }, {
            clientMessageId: userMessage.id
          })
        } catch (error) {
          throw new KorvoError(ERR_CHAT_500, 2, {
            message: `Failed to persist message: ${(error instanceof Error ? error.message : String(error))}`,
            status: 500,
          })
        }
        // Return early - no streaming
        sendInProgressRef.current = false
        setIsSending(false)
        return
      } else {
        // Custom streaming endpoint: persist first, then stream from custom endpoint
        // This allows packages with custom protocols to use their own streaming endpoint
        const persistResponse = await fetch(`${portalApiBase}/threads/${threadId}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role: 'user',
            content: userMessage.content,
            clientMessageId: userMessage.id
          })
        })
        if (!persistResponse.ok) {
          throw new KorvoError(ERR_CHAT_500, 3, {
            message: `Failed to persist message: ${persistResponse.status}`,
            status: persistResponse.status,
          })
        }

        // Build payload for custom streaming endpoint
        // Packages with custom protocols can format this however they need
        const payloadMessages = [
          {
            role: 'system',
            content: config.agent.systemPrompt
          },
          ...historyWithUser
            .filter(msg => msg.role !== 'tool_execution')
            .map(msg => ({
              role: msg.role === 'user' || msg.role === 'assistant' ? msg.role : 'assistant',
              content: msg.content !== undefined && msg.content !== null ? msg.content : ''
            }))
        ]

        // Use custom streaming endpoint (package-specific protocol)
        response = await fetch(config.portal.streamingEndpoint!, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            threadId,
            messages: payloadMessages,
            workspaceId: activeSession.workspaceId,
            toolPolicy: config.agent.toolPolicy || 'auto',
            temperature: config.agent.temperature || 0.7,
            maxTokens: config.agent.maxTokens || 2000,
            messageId: userMessage.id
          })
        })
      }

      if (!response.ok) {
        if (response.status === 404) {
          const errorData = await response.json().catch(() => ({}))
          if (errorData.code === 'THREAD_NOT_FOUND') {
            console.warn('Thread no longer exists, clearing session and retrying...')
            const clearedSession = {
              ...activeSession,
              threadId: null
            }
            persistSession(clearedSession)
            activeSession = clearedSession
            sendInProgressRef.current = false
            return handleSendMessage(overrideContent ?? content)
          }
        }
        // Try to get error details from response
        let errorMessage = `API request failed: ${response.status}`
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = `${errorMessage} - ${errorData.error}`
          }
        } catch {
          // If JSON parsing fails, use status text
          errorMessage = `${errorMessage} - ${response.statusText || 'Unknown error'}`
        }
        throw new KorvoError(ERR_CHAT_500, 4, {
          message: errorMessage,
          status: response.status,
        })
      }

      // Handle streaming response
      const reader = response.body?.getReader()
      if (!reader) {
        throw new KorvoError(ERR_CHAT_500, 5, {
          message: 'No response body',
          status: 500,
        })
      }
      streamReaderRef.current = reader

      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: '',
        timestamp: Date.now()
      }
      lastAssistantMessageIdRef.current = assistantMessage.id

      // Initialize streaming session tracking (best-effort resumable state)
      if (activeSession.threadId) {
        const streamingSession: StreamingSession = {
          threadId: activeSession.threadId,
          clientMessageId: userMessage.id,
          lastEventId: null,
          updatedAt: Date.now(),
          status: 'in_progress',
          ownerTabId: tabIdRef.current,
        }
        saveStreamingSessionToStorage(streamingSession)
      }

      // Add empty assistant message that we'll update
      // Note: Tool messages will be inserted before this assistant message as they arrive
      setMessages(prev => [...prev, assistantMessage])

      // Track assistant message content in a ref for callback
      const assistantContentRef = { current: '' }

      // Yield once so the disabled state paints before consuming the stream
      await new Promise(resolve => setTimeout(resolve, 0))

      // Read the stream (all endpoints; attach/replay SSE is used only for mid-stream reconnection)
      const decoder = new TextDecoder()
      let buffer = ''
      let receivedAnyContent = false

      while (true) {
        if (streamCancelledRef.current) {
          break
        }
         
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        { const popped = lines.pop(); buffer = popped !== undefined ? popped : ''; }

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            // Parse JSON separately so we don't swallow intentionally thrown errors
            let data: unknown
            try {
              const ssePayload = line.slice(6).trim()
              data = JSON.parse(ssePayload)
            } catch {
              console.warn('Failed to parse SSE data:', line)
              continue
            }

            // Update streaming session with latest eventId, if present
            const eventId = (data as { eventId?: unknown }).eventId
            if (eventId && activeSession && activeSession.threadId) {
              const priorSession = streamingSessionRef.current
              const nextSession: StreamingSession = {
                threadId: activeSession.threadId,
                clientMessageId: userMessage.id,
                lastEventId: String(eventId),
                updatedAt: Date.now(),
                status: 'in_progress',
                ownerTabId: tabIdRef.current,
              }
              // Preserve status if it was already marked completed/cancelled for some reason
              if (priorSession && priorSession.status !== 'in_progress') {
                nextSession.status = priorSession.status
              }
              saveStreamingSessionToStorage(nextSession)
            }

            if (typeof data !== 'object' || data === null) {
              continue
            }
            const payload = data as Record<string, unknown>

            // Thread title was updated on the server; update session so sidebar shows new title
            if (payload.type === 'thread.title' && typeof payload.title === 'string') {
              const newTitle = payload.title.trim()
              if (newTitle && activeSession) {
                setInternalSession(prev => prev ? { ...prev, title: newTitle, updatedAt: Date.now() } : prev)
                const updatedSession: ChatSession = { ...activeSession, title: newTitle, updatedAt: Date.now() }
                persistSession(updatedSession)
                activeSession = updatedSession
              }
              continue
            }

            // Unified handler for both query and helper agent messages
            if (payload.type === 'query_message' || payload.type === 'helper_message') {
              const agentType =
                typeof payload.agentType === 'string' && payload.agentType.length > 0
                  ? payload.agentType
                  : (payload.type === 'query_message' ? 'query' : 'helper')
              const payloadContent = typeof payload.content === 'string' ? payload.content : ''
              const payloadId = typeof payload.id === 'string' ? payload.id : undefined
              const payloadTs = typeof payload.timestamp === 'number' ? payload.timestamp : Date.now()
              console.log(`[KorvoChat] ========== ${agentType.toUpperCase()} MESSAGE RECEPTION ==========`)
              console.log(`[KorvoChat] Received ${agentType}_message SSE event`)
              console.log('[KorvoChat]   - Content preview:', payloadContent.substring(0, 100) + '...')
              console.log('[KorvoChat]   - Content length:', payloadContent.length || 0)
              console.log('[KorvoChat]   - Timestamp:', payloadTs)
              console.log('[KorvoChat]   - ID:', payloadId || 'generated')
              console.log('[KorvoChat]   - isAdmin:', isAdmin)
              
              // Create agent message object using unified structure
              // Ensure all flags are set correctly so isAgentMessage() recognizes it
              const agentMessage: ChatMessage & { 
                ragResults?: Array<{ content: string; title?: string; similarity?: number; sourceId?: string | number }>;
                isHelperMessage?: boolean;
                isQueryMessage?: boolean;
              } = {
                id: payloadId ?? `${agentType}-${Date.now()}-${Math.random()}`,
                role: agentType as 'query' | 'helper',
                content: payloadContent,
                timestamp: payloadTs,
                isHelperMessage: agentType === 'helper',
                isQueryMessage: agentType === 'query',
              }
              
              // Verify message will be recognized as agent message
              const willBeRecognized = agentMessage.role === 'query' || agentMessage.role === 'helper' || 
                                       agentMessage.isHelperMessage || agentMessage.isQueryMessage
              if (!willBeRecognized) {
                console.error(`[KorvoChat] ⚠️ Agent message will NOT be recognized!`, {
                  role: agentMessage.role,
                  isHelperMessage: agentMessage.isHelperMessage,
                  isQueryMessage: agentMessage.isQueryMessage,
                  agentType
                })
              }
              
              // Include RAG results for query messages (server sends RAG with query, not helper)
              if (agentType === 'query' && payload.ragResults && Array.isArray(payload.ragResults)) {
                agentMessage.ragResults = payload.ragResults as NonNullable<
                  ChatMessageAgentFlags['ragResults']
                >
                console.log('[KorvoChat]   - RAG results included:', payload.ragResults.length, 'result(s)')
              }
              
              // Insert agent message into messages list using unified logic
              // Position it after the most recent user message, but before assistant message
              // Query messages should appear before helper messages (handled by server order)
              setMessages(prev => {
                console.log(`[KorvoChat] Inserting ${agentType} message into state`)
                console.log('[KorvoChat]   - Current message count:', prev.length)
                console.log('[KorvoChat]   - Message roles:', prev.map(m => m.role).join(', '))
                
                // Check if agent message already exists (avoid duplicates)
                const existingAgent = prev.find(m => {
                  const flags = m as ChatMessageAgentFlags
                  return (
                    m.id === agentMessage.id ||
                    (agentType === 'helper' && flags.isHelperMessage === true && m.content === agentMessage.content) ||
                    (agentType === 'query' && flags.isQueryMessage === true && m.content === agentMessage.content)
                  )
                })
                if (existingAgent) {
                  console.log(`[KorvoChat] ⚠️ ${agentType} message already exists, skipping:`, agentMessage.id)
                  return prev
                }
                
                // Find insertion point: after last user message, but before assistant message
                const lastUserIndex = arrayFindLastIndex(prev, m => m.role === 'user')
                const firstAssistantIndex = prev.findIndex(m => m.role === 'assistant' && m.id === assistantMessage.id)
                
                if (lastUserIndex >= 0) {
                  const newMessages = [...prev]
                  let insertIndex = lastUserIndex + 1
                  
                  // If assistant message exists, insert before it
                  if (firstAssistantIndex >= 0 && firstAssistantIndex > insertIndex) {
                    insertIndex = firstAssistantIndex
                  }
                  
                  // For query messages: insert right after user, before helper and assistant
                  if (agentType === 'query') {
                    // Find if there are existing query messages and insert after them
                    const lastQueryIndex = arrayFindLastIndex(newMessages, m => {
                      const f = m as ChatMessageAgentFlags
                      return f.isQueryMessage === true || m.role === 'query'
                    })
                    if (lastQueryIndex >= insertIndex) {
                      insertIndex = lastQueryIndex + 1
                    }
                    newMessages.splice(insertIndex, 0, agentMessage)
                  } else {
                    // For helper messages: insert after query messages but before assistant
                    const lastQueryIndex = arrayFindLastIndex(newMessages, m => {
                      const f = m as ChatMessageAgentFlags
                      return f.isQueryMessage === true || m.role === 'query'
                    })
                    if (lastQueryIndex >= insertIndex) {
                      insertIndex = lastQueryIndex + 1
                    }
                    // Check if there are existing helper messages
                    const lastHelperIndex = arrayFindLastIndex(newMessages, m => {
                      const f = m as ChatMessageAgentFlags
                      return f.isHelperMessage === true || m.role === 'helper'
                    })
                    if (lastHelperIndex >= insertIndex) {
                      insertIndex = lastHelperIndex + 1
                    }
                    newMessages.splice(insertIndex, 0, agentMessage)
                  }
                  
                  console.log(`[KorvoChat] ✅ Inserted ${agentType} message at index`, insertIndex, 'of', newMessages.length, 'messages')
                  console.log(
                    '[KorvoChat]   - New message order:',
                    newMessages
                      .map((m) => {
                        const f = m as ChatMessageAgentFlags
                        return `${m.role}${f.isQueryMessage ? '(Q)' : ''}${f.isHelperMessage ? '(H)' : ''}`
                      })
                      .join(' -> '),
                  )
                  return newMessages
                }
                console.log(`[KorvoChat] ⚠️ Appending ${agentType} message to end (no user message found)`)
                return [...prev, agentMessage]
              })
              console.log('[KorvoChat] ===============================================')
              
              continue
            }

            if (payload.type === 'content' && payload.content) {
              // Stop typing indicator on first content chunk
              if (!receivedAnyContent) {
                receivedAnyContent = true
                setIsTyping(false)
                console.log('🔧 [KorvoChat] First content chunk received, assistant message streaming started')
              }
              // Update the assistant message with new content
              // Preserve tool messages when updating assistant content
              assistantContentRef.current += String(payload.content)
              setMessages(prev => prev.map(msg => 
                msg.id === assistantMessage.id 
                  ? { ...msg, content: assistantContentRef.current }
                  : msg
              ))
            } else if (payload.type === 'client_tool_call' || payload.type === 'client_tool_instruction') {
              console.log('🔧 [KorvoChat] Received client tool instruction:', payload)
              await executeClientTool(payload as ClientToolEventPayload)
            } else if (payload.type === 'tool_call_message' && payload.toolCall) {
              const rawToolCall = payload.toolCall
              const normalizedStreamTool = normalizeToolCall(rawToolCall)
              console.log('🔧 [KorvoChat] Received tool_call_message event:', {
                toolCallId: normalizedStreamTool.id,
                toolName: normalizedStreamTool.name,
                receivedAnyContent,
                assistantMessageContentLength: messages.find(m => m.id === assistantMessage.id)?.content.length || 0,
                timestamp: Date.now()
              })
              appendToolCallMessage(rawToolCall)
            } else if (payload.type === 'tool_call_update' && payload.toolCall) {
              const rawToolCall = payload.toolCall
              const normalizedStreamTool = normalizeToolCall(rawToolCall)
              console.log('🔧 [KorvoChat] Received tool_call_update event:', {
                toolCallId: normalizedStreamTool.id,
                toolName: normalizedStreamTool.name,
                hasResult: !!normalizedStreamTool.result,
                success: normalizedStreamTool.success,
                timestamp: Date.now()
              })
              applyToolCallUpdate(rawToolCall)
              // Call callback when tool execution completes
              if (onToolExecutionComplete && normalizedStreamTool.success !== undefined) {
                onToolExecutionComplete(normalizedStreamTool.name, normalizedStreamTool.success, normalizedStreamTool)
              }
            } else if (payload.type === 'tool_call_result') {
              const callIdStr =
                typeof payload.callId === 'string' && payload.callId.length > 0 ? payload.callId : `call-${Date.now()}`
              const toolNameStr =
                typeof payload.toolName === 'string' && payload.toolName.length > 0 ? payload.toolName : 'tool'
              const toolCall: ToolCall = {
                id: callIdStr,
                name: toolNameStr,
                arguments: (payload.args as Record<string, unknown>) ?? {},
                result: typeof payload.result === 'string' ? payload.result : undefined,
                success: true
              }
              applyToolCallUpdate(toolCall)
              // Call callback when tool execution completes
              if (onToolExecutionComplete) {
                onToolExecutionComplete(toolCall.name, true, toolCall)
              }
            } else if (payload.type === 'tool_call_error') {
              const callIdStrErr =
                typeof payload.callId === 'string' && payload.callId.length > 0 ? payload.callId : `call-${Date.now()}`
              const toolNameStrErr =
                typeof payload.toolName === 'string' && payload.toolName.length > 0 ? payload.toolName : 'tool'
              const toolCall: ToolCall = {
                id: callIdStrErr,
                name: toolNameStrErr,
                arguments: {},
                error: typeof payload.error === 'string' ? payload.error : undefined,
                success: false
              }
              applyToolCallUpdate(toolCall)
              // Call callback when tool execution completes
              if (onToolExecutionComplete) {
                onToolExecutionComplete(toolCall.name, false, toolCall)
              }
            } else if (payload.type === 'tool_call_progress') {
              const callIdProg =
                typeof payload.callId === 'string' && payload.callId.length > 0 ? payload.callId : `call-${Date.now()}`
              const toolNameProg =
                typeof payload.toolName === 'string' && payload.toolName.length > 0 ? payload.toolName : 'tool'
              applyToolCallUpdate(
                {
                  id: callIdProg,
                  name: toolNameProg,
                },
                {
                  progress: typeof payload.progress === 'number' ? payload.progress : undefined,
                  message: typeof payload.message === 'string' ? payload.message : undefined
                }
              )
            } else if (payload.type === 'tool_call_start') {
              const argsRaw = payload.args
              const argsParsed =
                typeof argsRaw === 'object' && argsRaw !== null && !Array.isArray(argsRaw)
                  ? (argsRaw as Record<string, unknown>)
                  : {}
              const callIdStart =
                typeof payload.callId === 'string' && payload.callId.length > 0 ? payload.callId : `call-${Date.now()}`
              const toolNameStart =
                typeof payload.toolName === 'string' && payload.toolName.length > 0 ? payload.toolName : 'tool'
              appendToolCallMessage({
                id: callIdStart,
                name: toolNameStart,
                arguments: argsParsed,
              })
            } else if (payload.type === 'tool_call') {
              appendToolCallMessage(payload.tool_call ?? payload.toolCall ?? payload)
            } else if (payload.type === 'error') {
              // Propagate streaming errors so outer catch triggers onError
              const streamingErrMsg =
                typeof payload.error === 'string' && payload.error.length > 0
                  ? payload.error
                  : 'Streaming error'
              throw new KorvoError(ERR_CHAT_500, 6, {
                message: streamingErrMsg,
                status: 500,
              })
            } else if (payload.type === 'done') {
              // Stream is complete - reset streaming state immediately
              setIsStreaming(false)
              isStreamingRef.current = false
              setIsTyping(false)
              setIsSending(false)
              saveStreamingSessionToStorage(null)
              // Do not break the `for (line of lines)` loop: one TCP chunk can contain
              // `data: {"type":"done"}` before `data: {"type":"client_tool_instruction",...}`.
              // Breaking would drop later events in the same batch (Docker: no POST /tools/client-response).
              continue
            }
          }

        // Yield to the event loop so timers/UI updates can run between chunks
         
        await new Promise(resolve => setTimeout(resolve, 0))
        }
      }

      // Empty response: replace assistant message with error
      if (!assistantContentRef.current.trim()) {
        const emptyKorvo = new KorvoError(ERR_CHAT_500, CHAT_EMPTY_RESPONSE_THROW_ID, {
          message: 'No response was received from the model.',
          status: 500,
        })
        const emptyResponseError = formatChatClientErrorMessage(emptyKorvo, { isAdmin: isAdmin === true })
        setMessages(prev => prev.map(msg =>
          msg.id === assistantMessage.id
            ? { ...msg, content: emptyResponseError, metadata: { ...msg.metadata, error: true, korvoId: emptyKorvo.id } }
            : msg
        ))
        onError?.(emptyKorvo)
      }

      // Call callback with final assistant message after stream completes
      if (onAssistantMessage && assistantContentRef.current.trim()) {
        const finalAssistantMessage: ChatMessage = {
          ...assistantMessage,
          content: assistantContentRef.current
        }
        onAssistantMessage(finalAssistantMessage)
      }

      onMessageSent?.(userMessage)
    } catch (error) {
      console.error('Failed to send message:', error)
      // If the network stream was interrupted while the provider continues
      // streaming in the background, schedule periodic history hydration so
      // the UI eventually shows the completed assistant answer once it is
      // persisted server-side.
      const clientErrorMessage = formatChatClientErrorMessage(error, { isAdmin: isAdmin === true })
      const korvoForMeta = error instanceof KorvoError ? error : ensureKorvoErrorForChat(error)
      
      const fallbackMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: clientErrorMessage,
        timestamp: Date.now(),
        metadata: {
          error: true,
          fallback: true,
          korvoId: korvoForMeta.id,
        }
      }
      setMessages(prev => [...prev, fallbackMessage])
      
      // Also call onError callback if provided
      onError?.(error as Error)
    } finally {
      sendInProgressRef.current = false
      streamReaderRef.current = null
      streamCancelledRef.current = false
      setIsStreaming(false)
      isStreamingRef.current = false
      setIsSending(false)
      setIsTyping(false)
      // Return focus to the input after sending completes
      // Defer to next tick to ensure DOM updates are flushed
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }

  // Message actions (edit: useKorvoChatMessageEditing)

  const handleDeleteMessage = (messageId: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== messageId))
  }

  const handleCopyMessage = async (messageId: string) => {
    const message = messages.find(m => m.id === messageId)
    if (!message) return

    try {
      await navigator.clipboard.writeText(message.content)
      setCopiedMessageId(messageId)
      setTimeout(() => setCopiedMessageId(null), 2000)
    } catch (error) {
      console.error('Failed to copy message:', error)
    }
  }

  const handleShareThread = async () => {
    if (!currentSession?.threadId) return

    try {
      // Update thread visibility to public
      const moduleName = config.portal.packageName
      const response = await fetch(`/api/${moduleName}/threads/${currentSession.threadId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility: 'public' })
      })

      if (!response.ok) {
        throw new KorvoError(ERR_CHAT_500, 7, {
          message: 'Failed to update thread visibility',
          status: response.status,
        })
      }

      // Generate shareable link
      const shareUrl = `${window.location.origin}/thread/${currentSession.threadId}`
      
      // Copy to clipboard
      await navigator.clipboard.writeText(shareUrl)
      setThreadVisibility('public')
      setIsShareLinkCopied(true)
      setTimeout(() => setIsShareLinkCopied(false), 3000)
    } catch (error) {
      console.error('Failed to share thread:', error)
      onError?.(error as Error)
    }
  }

  const handleShowDebugInfo = async (messageId: string) => {
    await runKorvoChatMessageDebugFlow(
      messageId,
      messages,
      currentSession,
      threadsClient,
      config,
      (data) => setDebugInfo(data as DebugInfo),
      () => setShowDebugModal(true),
    )
  }

  // Reset conversation
  const handleResetConversation = () => {
    setShowResetConfirm(true)
  }

  const confirmResetConversation = () => {
    // Clear messages
    setMessages([])
    toolMessageMapRef.current.clear()
    
    // Clear attached files
    setAttachedFiles([])
    
    // Clear editing state
    setEditingMessageId(null)
    setEditingContent('')
    
    // localStorage removed - clearing handled externally
    
    // Create new session - preserve initialSession.id if provided (for stable session IDs)
    const sessionId = initialSessionId || `korvochat-${Date.now()}`
    const createdSession: ChatSession = {
      id: sessionId,
      title: 'KorvoChat Session',
      threadId: null, // Will be created when first message is sent, then session.id will be updated to thread.id
      createdAt: Date.now(),
      updatedAt: Date.now(),
      metadata: {
        packageName: config.portal.packageName,
        systemName: config.portal.systemName,
        systemPrompt: config.agent.systemPrompt
      }
    }
    persistSession(createdSession)
    
    // Focus input
    setTimeout(() => {
      inputRef.current?.focus()
    }, 100)
    
    setShowResetConfirm(false)
  }

  const cancelResetConversation = () => {
    setShowResetConfirm(false)
  }

  const handleRegenerateResponse = async (messageId: string) => {
    const messageIndex = messages.findIndex(m => m.id === messageId)
    if (messageIndex === -1) return

    // Find the user message that prompted this response
    const userMessage = messages[messageIndex - 1]
    if (!userMessage || userMessage.role !== 'user') return

    // Remove the assistant message
    setMessages(prev => prev.filter(msg => msg.id !== messageId))
    
    // Resend the user message
    setIsThreadLoading(true)
    setIsTyping(true)

    try {
      // Ensure we have a thread ID
      const threadId = currentSession?.threadId
      if (!threadId) {
        throw new KorvoError(ERR_BAD_REQUEST_400, 2, {
          message: 'No thread ID available for regeneration',
          status: 400,
        })
      }

      // Send message to the thread using ThreadsClient
      const stream = await threadsClient.sendMessage(threadId, {
        role: 'user',
        content: userMessage.content
      }, {
        clientMessageId: userMessage.id
      })
      // Convert ReadableStream to Response-like object for compatibility
      const response = {
        ok: true,
        status: 200,
        body: stream,
        headers: new Headers({ 'Content-Type': 'text/event-stream' }),
        json: async () => ({}),
        text: async () => '',
      } as Response

      if (!response.ok) {
        throw new KorvoError(ERR_CHAT_500, 8, {
          message: `API request failed: ${response.status}`,
          status: response.status,
        })
      }

      // Handle streaming response
      const reader = response.body?.getReader()
      if (!reader) {
        throw new KorvoError(ERR_CHAT_500, 9, {
          message: 'No response body',
          status: 500,
        })
      }

      const assistantMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: '',
        timestamp: Date.now()
      }

      // Add empty assistant message that we'll update
      setMessages(prev => [...prev, assistantMessage])

      // Read the stream
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
         
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        { const popped = lines.pop(); buffer = popped !== undefined ? popped : ''; }

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const ssePayload = line.slice(6).trim()
              const data = JSON.parse(ssePayload)
              
              if (data.type === 'content' && data.content) {
                // Update the assistant message with new content
                setMessages(prev => prev.map(msg => 
                  msg.id === assistantMessage.id 
                    ? { ...msg, content: msg.content + data.content }
                    : msg
                ))
              } else if (data.type === 'tool_call') {
                // Handle tool calls if needed
                console.log('Tool call:', data)
              } else if (data.type === 'done') {
                // Stream is complete; keep scanning this batch (same ordering caveat as handleSendMessage).
                continue
              }
            } catch {
              // Ignore malformed JSON
              console.warn('Failed to parse SSE data:', line)
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to regenerate response:', error)
      
      const fallbackMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: 'I regenerated my response. This is a fallback response since the API is not available.',
        timestamp: Date.now()
      }
      
      setMessages(prev => [...prev, fallbackMessage])
    } finally {
      setIsThreadLoading(false)
      setIsTyping(false)
    }
  }

  handleSendMessageRef.current = handleSendMessage
  cancelResetConversationRef.current = cancelResetConversation
  confirmResetConversationRef.current = confirmResetConversation
  handleResetConversationRef.current = handleResetConversation
  setShowDebugRef.current = setShowDebug

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Prevent key handling when disabled (defensive check using ref for immediate access)
    if (isSending || isThreadLoading || isStreaming || isStreamingRef.current || isOffline) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (editingMessageId) {
        handleSaveEdit()
      } else {
        void handleSendMessage()
      }
    } else if (e.key === 'Escape' && editingMessageId) {
      handleCancelEdit()
    }
  }

  // Keyboard shortcuts (document listener)
  useKorvoChatDocumentKeydown(
    {
      showResetConfirm,
      debugPanelOpen: showDebug,
      closeDebugPanel: () => {
        setShowDebug(false)
      },
      editingMessageId,
      debugFeatureEnabled: config.features.debug,
      resetConversationFeatureEnabled: config.features.resetConversation,
      cancelResetConversation: () => {
        cancelResetConversationRef.current()
      },
      confirmResetConversation: () => {
        confirmResetConversationRef.current()
      },
      clearEditingMessage: () => {
        setEditingMessageId(null)
        setEditingContent('')
      },
      sendMessage: () => {
        void handleSendMessageRef.current()
      },
      composerInputRef: inputRef,
      toggleDebug: () => {
        setShowDebugRef.current((prev) => !prev)
      },
      openResetConversation: () => {
        handleResetConversationRef.current()
      },
    },
    editingMessageId,
    showDebug,
    config.features.debug,
    config.features.resetConversation,
    showResetConfirm,
  )

  const getAIAvatar = () => {
    if (config.ui.aiAvatar) {
      if (typeof config.ui.aiAvatar === 'string') {
        return <Image src={config.ui.aiAvatar} alt="AI" width={32} height={32} className="w-8 h-8 rounded-full" />
      } else {
        return (
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold"
            style={{ backgroundColor: config.ui.aiAvatar.color }}
          >
            {config.ui.aiAvatar.letter}
          </div>
        )
      }
    }
    return <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">AI</div>
  }

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString()
  }

  // Calculate viewport-based height if requested
  const viewportHeightStyle = useMemo(() => {
    if (!viewportHeight) return {}
    
    const config = typeof viewportHeight === 'object' ? viewportHeight : {}
    const offset = config.offset || '20rem'
    const minHeight = config.minHeight || '400px'
    const maxHeight = config.maxHeight || '800px'
    
    return {
      height: `calc(100vh - ${offset})`,
      minHeight,
      maxHeight,
    }
  }, [viewportHeight])

  const composerDisabled =
    isReadOnly ||
    isSending ||
    isThreadLoading ||
    isStreaming ||
    isStreamingRef.current ||
    isOffline ||
    !isThreadReady

  const composerPlaceholder = hasThreadId
    ? config.features.fileUpload
      ? 'Type or drag files...'
      : 'Type your message...'
    : 'Select or create a chat thread to enable messaging.'

  return (
    <>
      <style>{`
        .korvochat-user-text {
          color: white !important;
        }
        .korvochat-message-content,
        .korvochat-message-content p {
          background: transparent !important;
        }
        .korvochat-timestamp {
          background: transparent !important;
          background-color: transparent !important;
        }
        .korvochat-debug-modal-close {
          color: var(--foreground-muted);
          transition: color 0.15s ease;
        }
        .korvochat-debug-modal-close:hover {
          color: var(--foreground-default);
        }
        .korvochat-debug-copy-primary {
          background-color: var(--primary-default);
          color: var(--primary-foreground);
          transition: background-color 0.15s ease;
        }
        .korvochat-debug-copy-primary:hover {
          background-color: var(--primary-hover);
        }
        .korvochat-debug-copy-success {
          background-color: var(--success-default);
          color: var(--success-foreground);
          transition: opacity 0.15s ease;
        }
        .korvochat-debug-copy-success:hover {
          opacity: 0.9;
        }
      `}</style>
      <div 
        className={`flex flex-col h-full overflow-hidden min-h-0 ${className} ${isDragOver ? 'korvo-bg-primary-opacity-10' : ''} relative`} 
        style={{ 
          height: viewportHeight ? undefined : '100%',
          backgroundColor: 'var(--background-default)',
          ...style,
          ...viewportHeightStyle,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      {/* Initialization Loading Overlay */}
      {isInitializing && (
        <div className="absolute inset-0 flex items-center justify-center bg-overlay-background backdrop-blur-sm z-50">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Workbench often hides the chat header; admins still need the debug entry (US-AM-34). */}
      {!(config.ui.showHeader ?? true) && !isMobileViewport && config.features.debug && isAdmin && (
        <div className="absolute right-2 top-1 z-30">
          <button
            type="button"
            data-testid="debug-panel-toggle"
            onClick={() => setShowDebug(!showDebug)}
            className="rounded-md px-3 py-1 text-sm transition-colors"
            style={{ backgroundColor: 'var(--foreground-muted)', color: 'var(--primary-foreground)' }}
          >
            {showDebug ? 'Hide Debug' : 'Show Debug'}
          </button>
        </div>
      )}
      
      {/* Header: hidden on mobile to save space; debug moves to context menu */}
      {(config.ui.showHeader ?? true) && !isMobileViewport && (
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-muted)' }}>
          <div className="flex items-center space-x-3">
            {config.ui.showAvatars ? getAIAvatar() : null}
            <div>
              <h3 className="text-lg font-semibold text-foreground-default">KorvoChat</h3>
              <p className="text-sm text-foreground-muted">
                {config.agent.mode === 'single' ? 'Single Agent Mode' : 'Dual Agent Mode'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Share Button */}
            {config.features.shareable && currentSession?.threadId && (
              <button
                onClick={() => { void handleShareThread() }}
                className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors flex items-center space-x-1"
                title={isShareLinkCopied ? 'Link copied!' : 'Share conversation'}
              >
                {isShareLinkCopied ? (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span>Share</span>
                  </>
                )}
              </button>
            )}
            
            {/* Reset Conversation Button */}
            {config.features.resetConversation && (
              <button
                onClick={handleResetConversation}
                className="px-3 py-1 text-sm bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                title="Reset conversation"
              >
                🔄 Reset
              </button>
            )}
            
            {/* Debug Toggle (Admin Only) - desktop only; on mobile see context menu below */}
            {config.features.debug && isAdmin && (
              <button
                type="button"
                data-testid="debug-panel-toggle"
                onClick={() => setShowDebug(!showDebug)}
                className="px-3 py-1 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded-md transition-colors"
              >
                {showDebug ? 'Hide Debug' : 'Show Debug'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Mobile: context menu trigger (kebab) with Show Debug for admins */}
      {isMobileViewport && config.features.debug && isAdmin && (
        <div className="absolute top-2 right-2 z-30 flex items-center justify-end">
          <button
            ref={mobileMenuButtonRef}
            type="button"
            onClick={() => setMobileContextMenuOpen((prev) => !prev)}
            className="p-2 rounded-md transition-colors"
            style={{ color: 'var(--foreground-muted)', backgroundColor: 'transparent' }}
            aria-label="Chat options"
            aria-expanded={mobileContextMenuOpen}
            aria-haspopup="true"
            data-testid="korvochat-mobile-context-menu-button"
          >
            <ModuleIcon icon="ri-more-2-fill" className="h-5 w-5" style={{ color: 'inherit' }} />
          </button>
          {mobileContextMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                aria-hidden
                onClick={() => setMobileContextMenuOpen(false)}
              />
              <div
                ref={mobileMenuRef}
                className="absolute right-0 top-full mt-1 py-1 rounded-lg shadow-lg border z-50 min-w-[160px]"
                style={{
                  backgroundColor: 'var(--background-secondary)',
                  borderColor: 'var(--border-default)',
                }}
                role="menu"
                data-testid="korvochat-mobile-context-menu"
              >
                <button
                  type="button"
                  role="menuitem"
                  data-testid="debug-panel-toggle-mobile"
                  className="w-full text-left px-3 py-2.5 text-sm rounded-none transition-colors"
                  style={{ color: 'var(--foreground-default)' }}
                  onClick={() => {
                    setShowDebug((prev) => !prev)
                    setMobileContextMenuOpen(false)
                  }}
                >
                  {showDebug ? 'Hide Debug' : 'Show Debug'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Offline notification */}
      {isOffline && (
        <div className="bg-danger-default/20 border-l-4 border-danger-default text-danger-default p-4 m-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm">
                Network Connection Error: Offline
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Drag Overlay */}
      {isDragOver && (
        <div className="absolute inset-0 korvo-bg-primary-opacity-20 flex items-center justify-center z-50">
          <div className="bg-background-secondary rounded-lg p-8 shadow-lg border-2 border-dashed border-primary-default">
            <div className="text-center">
              <div className="text-4xl mb-4">📁</div>
              <p className="text-lg font-medium text-foreground-default">Drop files here to upload</p>
              <p className="text-sm text-foreground-muted">Maximum file size: 10MB</p>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Dialog */}
      {showResetConfirm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reset-dialog-title"
          aria-describedby="reset-dialog-description"
        >
          <div className="bg-background-secondary rounded-lg p-6 shadow-lg max-w-md mx-4">
            <div className="text-center">
              <div className="text-4xl mb-4" aria-hidden="true">⚠️</div>
              <h3 id="reset-dialog-title" className="text-lg font-semibold text-foreground-default mb-2">
                Reset Conversation
              </h3>
              <p id="reset-dialog-description" className="text-sm text-foreground-muted mb-6">
                This will clear all messages and start a new conversation. This action cannot be undone.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={cancelResetConversation}
                  className="flex-1 px-4 py-2 text-sm bg-foreground-muted text-primary-foreground hover:opacity-80 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-primary-default"
                  autoFocus
                >
                  Cancel
                </button>
                <button
                  onClick={confirmResetConversation}
                  className="flex-1 px-4 py-2 text-sm bg-danger-default hover:opacity-90 text-danger-foreground rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-danger-default"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="flex flex-1 overflow-hidden min-h-0"
        style={{
          backgroundColor: 'var(--background-default)',
          minHeight: 0,
          flex: '1 1 0%',
        }}
      >
        {/* Main Chat Area - min-h-0 so flex child fills column in nested layout (e.g. editor panel) */}
        <div
          className="flex-1 flex flex-col overflow-hidden min-h-0"
          style={{ minHeight: 0, flex: '1 1 0%' }}
        >
          {/* Messages */}
          <div 
            ref={messagesContainerRef}
            data-testid="korvochat-messages-container"
            className={`flex-1 overflow-y-auto p-4 space-y-4 relative${!hasThreadId && !isThreadLoading ? ' pointer-events-none' : ''}`}
            style={{ flex: '1 1 0%', minHeight: 0 }}
          >
            {isThreadLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-overlay-background backdrop-blur-sm z-10">
                <LoadingSpinner size="lg" />
              </div>
            )}

            {!hasThreadId && !isThreadLoading ? (
              <div className="pointer-events-none text-center text-foreground-muted py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-skeleton-base rounded-full flex items-center justify-center">
                  <span className="text-2xl">💬</span>
                </div>
                <p className="text-lg font-medium">Select or create a chat thread</p>
                <p className="text-sm">Choose a thread or tap New Chat to start messaging.</p>
              </div>
            ) : (
              <>
                {messages.length === 0 && !isThreadLoading && (
                  <div className="pointer-events-none text-center text-foreground-muted py-8">
                    <div className="w-16 h-16 mx-auto mb-4 bg-skeleton-base rounded-full flex items-center justify-center">
                      <span className="text-2xl">💬</span>
                    </div>
                    <p className="text-lg font-medium">Start a conversation</p>
                    <p className="text-sm">Type below to begin chatting</p>
                  </div>
                )}
                {(() => {
              return messages.map((message, messageIndex) => {
              // Special rendering for summary marker messages
              if (message.role === 'summary_marker') {
                const summaryMetadata = message.toolCalls?.[0] as { 
                  kind?: string
                  model?: string
                  tokenThreshold?: number
                  totalTokens?: number
                  summarizedMessageCount?: number
                  createdAt?: string
                } | undefined
                
                return (
                  <div
                    key={message.id}
                    className="flex justify-start group"
                  >
                    <div className="flex items-start space-x-3 max-w-2xl w-full">
                      {/* Summary Icon */}
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center">
                          <span className="text-sm">📝</span>
                        </div>
                      </div>
                      
                      {/* Summary Content */}
                      <div className="flex-1 min-w-0">
                        <details className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                          <summary className="cursor-pointer font-medium text-purple-900 text-sm">
                            Chat summarized ({summaryMetadata?.summarizedMessageCount || 0} messages)
                          </summary>
                          <div className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">
                            {message.content}
                          </div>
                          {summaryMetadata && (
                            <div className="korvochat-timestamp mt-2 text-xs text-foreground-muted">
                              <div>Model: {summaryMetadata.model || 'unknown'}</div>
                              <div>Tokens: {summaryMetadata.totalTokens || 0} / {summaryMetadata.tokenThreshold || 8000}</div>
                            </div>
                          )}
                        </details>
                        {config.ui.showTimestamps && (
                          <p className="korvochat-timestamp text-xs mt-1 text-foreground-muted">
                            {formatTimestamp(message.timestamp)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
              
              // Special rendering for tool execution messages
              if (message.role === 'tool_execution') {
                // ToolCall format is already flat (id, name, arguments, result, success, error)
                const toolCallsForDisplay = (message.toolCalls || []).map(tc => {
                  let parsedArgs: Record<string, unknown> = {}
                  try {
                    // Arguments might be a string (JSON) or already an object
                    if (typeof tc.arguments === 'string') {
                      parsedArgs = JSON.parse(tc.arguments)
                    } else if (tc.arguments && typeof tc.arguments === 'object') {
                      parsedArgs = tc.arguments as Record<string, unknown>
                    }
                  } catch {
                    parsedArgs = {}
                  }
                  
                  return {
                    id: tc.id,
                    name: tc.name,
                    arguments: parsedArgs,
                    result: tc.result,
                    success: tc.success,
                    error: tc.error
                  }
                })
                
                return (
                  <div
                    key={message.id}
                    className="flex justify-start group"
                  >
                    <div className="flex items-start space-x-3 max-w-2xl w-full">
                      {/* Tool Icon */}
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center">
                          <span className="text-sm">🔧</span>
                        </div>
                      </div>
                      
                      {/* Tool Call Content */}
                      <div className="flex-1 min-w-0">
                        <ToolCallMessage 
                          toolCalls={toolCallsForDisplay} 
                          content={message.content} 
                        />
                        {config.ui.showTimestamps && (
                          <p className="korvochat-timestamp text-xs mt-1 text-foreground-muted">
                            {formatTimestamp(message.timestamp)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              }
              
              // Agent message rendering (query and helper - admin only - server already validated)
              if (isAgentMessage(message)) {
                // Only render if admin (debug toggle is optional - if messages exist, show them for admins)
                // Server already validated admin status before sending agent messages
                // If we received an agent message, the user is admin, but check client-side admin status too
                if (!isAdminForMessages || !showDebug) {
                  return null;
                }
                
                return (
                  <AgentMessage
                    key={message.id}
                    message={message as ChatMessageAgentFlags}
                    onShowDebugInfo={handleShowDebugInfo}
                    showTimestamps={config.ui.showTimestamps ?? false}
                    formatTimestamp={formatTimestamp}
                  />
                )
              }

              // Hidden user messages (<%...%> or metadata.hidden or config pattern) - do not render unless admin+debug
              const isHidden = isHiddenUserMessageUtil(message, config.ui.hiddenUserMessagePattern)
              const showHiddenInDebug = isAdmin && showDebug && config.features.debug
              if (isHidden && !showHiddenInDebug) {
                return <React.Fragment key={message.id} />
              }

              // Skip rendering empty last-assistant message while typing (thinking phase): show only typing indicator, not empty bubble + cursor
              const isLastMessage = messageIndex === messages.length - 1
              if (isLastMessage && message.role === 'assistant' && String(message.content !== null && message.content !== undefined ? message.content : '').trim() === '' && isTyping) {
                return <React.Fragment key={message.id} />
              }

              const lastAssistantIdxForCard = messages
                .map((m, idx) => (m.role === 'assistant' ? idx : -1))
                .filter((i) => i !== -1)
                .pop();
              const isLastAssistantForCard =
                lastAssistantIdxForCard !== undefined && messageIndex === lastAssistantIdxForCard;
              const skipCardParsing =
                message.role === 'assistant' &&
                (isStreaming || isStreamingRef.current) &&
                isLastAssistantForCard;

              // Regular message rendering
              return (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} group`}
                >
                  <div className={`flex items-start space-x-2 max-w-xs lg:max-w-md`}>
                    {message.role === 'assistant' && config.ui.showAvatars && (
                      <div className="flex-shrink-0 mt-1">
                        {getAIAvatar()}
                      </div>
                    )}
                    
                    <div className="flex flex-col">
                      <div
                        className="px-4 py-2 rounded-lg"
                        style={
                          message.role === 'user'
                            ? { backgroundColor: 'var(--primary-default)', color: 'var(--primary-foreground)' }
                            : {
                                backgroundColor: 'var(--background-muted)',
                                color: 'var(--foreground-default)',
                                border: '1px solid var(--border-default)',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                              }
                        }
                      >
                        {editingMessageId === message.id ? (
                          <div className="space-y-2">
                            <textarea
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              onKeyPress={handleKeyPress}
                              className="w-full bg-transparent border-none outline-none resize-none text-sm"
                              rows={3}
                              autoFocus
                            />
                            <div className="flex space-x-2">
                              <button
                                onClick={handleSaveEdit}
                                className="px-2 py-1 text-xs bg-success-default hover:opacity-90 rounded text-success-foreground"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-2 py-1 text-xs bg-foreground-muted hover:opacity-80 rounded text-primary-foreground"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p
                              className={`korvochat-message-content text-sm whitespace-pre-wrap bg-transparent ${message.role === 'user' ? 'korvochat-user-text text-primary-foreground' : 'text-foreground-default'}`}
                            >
                            <ChatMessageBodyContent content={message.content} skipCardParsing={skipCardParsing} />
                            {/* Show streaming indicator (blinking cursor) at end of last assistant message when streaming */}
                            {message.role === 'assistant' && (isStreaming || isStreamingRef.current) && (() => {
                              // Check if this is the last assistant message by finding the last assistant index
                              const lastAssistantIndex = messages.map((m, idx) => m.role === 'assistant' ? idx : -1).filter(i => i !== -1).pop()
                              const isLastAssistant = lastAssistantIndex !== undefined && messageIndex === lastAssistantIndex
                              return isLastAssistant ? (
                                <span 
                                  className="inline-block w-1 h-4 bg-gray-600 ml-1 align-middle animate-pulse" 
                                  style={{ 
                                    animation: 'blink 1s step-end infinite',
                                    opacity: 1,
                                    width: '2px',
                                    height: '1em',
                                    backgroundColor: 'var(--foreground-muted)'
                                  }}
                                  data-testid="streaming-cursor"
                                />
                              ) : null
                            })()}
                          </p>
                            
                            {/* File attachments */}
                            {message.files && message.files.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {message.files.map((file, fileIndex) => (
                                  <div key={fileIndex} className={`text-xs px-2 py-1 rounded ${
                                    message.role === 'user' 
                                      ? 'bg-primary text-primary-foreground' 
                                      : 'bg-skeleton-base text-foreground-default'
                                  }`}>
                                    📎 {file.name} ({formatFileSize(file.size)})
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {config.ui.showTimestamps && (
                              <p className={`korvochat-timestamp text-xs mt-1 ${
                                message.role === 'user' ? 'korvochat-user-text' : 'text-foreground-muted'
                              }`}>
                                {formatTimestamp(message.timestamp)}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                      
                      {/* Message actions */}
                      <div className={`flex space-x-1 mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      }`}>
                        {message.role === 'user' && (
                          <>
                            <button
                              onClick={() => handleEditMessage(message.id)}
                              className="px-2 py-1 text-xs bg-skeleton-base hover:bg-panel-hover rounded text-foreground-default"
                              title="Edit message"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => handleDeleteMessage(message.id)}
                              className="px-2 py-1 text-xs bg-danger-default/20 hover:bg-danger-default/30 rounded text-danger-default"
                              title="Delete message"
                            >
                              🗑️
                            </button>
                            <button
                              onClick={() => { void handleCopyMessage(message.id) }}
                              className="px-2 py-1 text-xs bg-success-default/20 hover:bg-success-default/30 rounded text-success-default"
                              title="Copy message"
                            >
                              {copiedMessageId === message.id ? (
                                <CheckIcon className="w-3 h-3" />
                              ) : (
                                <CopyIcon className="w-3 h-3" />
                              )}
                            </button>
                            {config.features.debug && (
                            <button
                              onClick={() => { void handleShowDebugInfo(message.id); }}
                                className="px-2 py-1 text-xs bg-purple-200 hover:bg-purple-300 rounded text-purple-700"
                                title="Show debug info"
                                aria-label="Show debug info"
                              >
                                <InfoIcon className="w-3 h-3" />
                              </button>
                            )}
                          </>
                        )}
                        {message.role === 'assistant' && (
                          <>
                            <button
                              onClick={() => { void handleRegenerateResponse(message.id) }}
                              className="px-2 py-1 text-xs bg-blue-200 hover:bg-blue-300 rounded text-blue-700"
                              title="Regenerate response"
                              aria-label="Regenerate response"
                            >
                              🔄
                            </button>
                            <button
                              onClick={() => { void handleCopyMessage(message.id) }}
                              className="px-2 py-1 text-xs bg-success-default/20 hover:bg-success-default/30 rounded text-success-default"
                              title="Copy message"
                              aria-label={copiedMessageId === message.id ? 'Message copied' : 'Copy message'}
                            >
                              {copiedMessageId === message.id ? (
                                <CheckIcon className="w-3 h-3" />
                              ) : (
                                <CopyIcon className="w-3 h-3" />
                              )}
                            </button>
                            {config.features.debug && (
                              <button
                                onClick={() => { void handleShowDebugInfo(message.id); }}
                                className="px-2 py-1 text-xs bg-purple-200 hover:bg-purple-300 rounded text-purple-700"
                                title="Show debug info"
                                aria-label="Show debug info"
                              >
                                <InfoIcon className="w-3 h-3" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {message.role === 'user' && config.ui.showAvatars && (
                      <div className="flex-shrink-0 mt-1">
                        <UserAvatarBubble
                          name={
                            currentUserProfile?.displayName ||
                            currentUserProfile?.name ||
                            currentUserProfile?.email ||
                            'User'
                          }
                          email={currentUserProfile?.email}
                          avatarUrl={currentUserProfile?.avatarUrl ?? null}
                          size={40}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )
              })
            })()}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start" data-testid="korvochat-typing-indicator">
                <div className="flex items-start space-x-2">
                  {config.ui.showAvatars && (
                    <div className="flex-shrink-0 mt-1">
                      {getAIAvatar()}
                    </div>
                  )}
                  <div className="bg-gray-100 px-4 py-2 rounded-lg">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

            <div ref={messagesEndRef} data-testid="korvochat-messages-end" />
            
            {/* Scroll to Bottom Button */}
            {showScrollToBottom && (
              <button
                onClick={() => scrollToBottom()}
                className="fixed bottom-[9rem] right-6 md:bottom-20 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 z-10"
                title="Scroll to bottom"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </button>
            )}
          </div>

          {/* Input Area — hidden when hideComposer (composer in shell slot) */}
          {!hideComposer && (
          <div className="p-4 w-full min-w-0" style={{ width: '100%', backgroundColor: 'var(--background-secondary)', borderTop: '1px solid var(--border-muted)' }}>
            {/* Attached Files */}
            {attachedFiles.length > 0 && (
              <div className="mb-3 p-2 bg-skeleton-base rounded-md">
                <div className="text-xs text-foreground-muted mb-2">Attached files:</div>
                <div className="flex flex-wrap gap-2">
                  {attachedFiles.map((file, index) => (
                    <div key={index} className="flex items-center space-x-2 bg-background-default px-2 py-1 rounded border border-border-default text-xs text-foreground-default">
                      <span>📎</span>
                      <span className="truncate max-w-32">{file.name}</span>
                      <span className="text-foreground-muted">({formatFileSize(file.size)})</span>
                      <button
                        onClick={() => removeFile(index)}
                        className="text-danger-default hover:opacity-80"
                        disabled={isReadOnly || isSending || isThreadLoading || isOffline}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex space-x-2 w-full min-w-0" style={{ width: '100%' }}>
              {/* File Upload Button */}
              {config.features.fileUpload && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={composerDisabled}
                  className="px-3 py-2 text-foreground-muted hover:text-foreground-default hover:bg-panel-hover rounded-md transition-colors disabled:opacity-50"
                  title="Attach files"
                >
                  📎
                </button>
              )}
              
            <textarea
                ref={inputRef}
                data-testid="korvochat-input"
                value={input}
                onChange={handleInputChange}
                onInput={handleInput}
                onKeyPress={handleKeyPress}
                aria-label="Chat input"
                placeholder={composerPlaceholder}
                disabled={composerDisabled}
                readOnly={composerDisabled}
                className="flex-1 resize-none border border-border-default rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-transparent bg-input-background text-input-foreground placeholder:text-input-placeholder disabled:bg-skeleton-base disabled:cursor-not-allowed"
                rows={1}
                style={{ flex: '1 1 0%', minWidth: 0, minHeight: '40px', maxHeight: '120px' }}
                maxLength={maxMessageLength > 0 ? maxMessageLength : undefined}
              />
              {isStreaming || isStreamingRef.current || isSending ? (
                <button
                  onClick={handleStopStreaming}
                  className="px-4 py-2 bg-danger-default text-danger-foreground rounded-md hover:opacity-90 disabled:bg-foreground-muted disabled:cursor-not-allowed transition-colors"
                >
                  Stop
                </button>
              ) : (
              <button
                  onClick={() => { void handleSendMessage() }}
                disabled={
                    composerDisabled ||
                    (!input.trim() && attachedFiles.length === 0) ||
                    isInputOverLimit ||
                    isSending ||
                    isThreadLoading ||
                    isStreaming ||
                    isStreamingRef.current ||
                    isOffline
                  }
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary-hover disabled:bg-foreground-muted disabled:cursor-not-allowed transition-colors"
                >
                  Send
                </button>
              )}
            </div>
            
            {/* Hidden file input */}
            {config.features.fileUpload && (
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json"
              />
            )}
            
            {maxMessageLength > 0 && (
              <div className="mt-1 flex items-center justify-between text-xs text-foreground-muted" data-testid="char-count">
                <span>
                  {isInputOverLimit ? 'Message too long' : ''}
                </span>
                <span>
                  {input.length}/{maxMessageLength}
                </span>
              </div>
            )}
          </div>
          )}
        </div>

        {/* Debug Panel (modal — US-AM-34 integration + deploy-smoke contract) */}
        {showDebug && config.features.debug && (
          <div
            data-testid="debug-panel-overlay"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'var(--overlay-background)' }}
            onClick={() => setShowDebug(false)}
            role="presentation"
          >
            <div
              data-testid="debug-panel-content"
              role="dialog"
              aria-modal="true"
              aria-labelledby="korvo-debug-panel-title"
              className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border shadow-lg"
              style={{
                backgroundColor: 'var(--background-secondary)',
                borderColor: 'var(--border-default)',
                color: 'var(--foreground-default)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="flex shrink-0 items-center justify-between border-b px-4 py-3"
                style={{ borderColor: 'var(--border-default)' }}
              >
                <h4 id="korvo-debug-panel-title" className="m-0 font-semibold" style={{ color: 'var(--foreground-default)' }}>
                  Debug Panel
                </h4>
                <button
                  type="button"
                  data-testid="debug-panel-close"
                  className="rounded px-2 py-1 text-xl leading-none"
                  style={{ color: 'var(--foreground-muted)' }}
                  aria-label="Close"
                  onClick={() => setShowDebug(false)}
                >
                  ×
                </button>
              </div>
              <div
                data-testid="debug-panel-scroll-container"
                className="min-h-0 flex-1 overflow-y-auto px-4 py-3 text-sm"
                style={{ color: 'var(--foreground-default)' }}
              >
                {(() => {
                  const session = currentSession
                  const md = session?.metadata
                  const metaStr = (key: string): string => {
                    if (!md || !(key in md)) {
                      return 'N/A'
                    }
                    const v = md[key]
                    if (v === null || v === undefined) {
                      return 'N/A'
                    }
                    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
                      return String(v)
                    }
                    try {
                      return JSON.stringify(v)
                    } catch {
                      return 'N/A'
                    }
                  }
                  const formatTs = (ts: number | undefined): string => {
                    if (ts === undefined || !Number.isFinite(ts)) {
                      return 'N/A'
                    }
                    try {
                      return new Date(ts).toLocaleString(undefined, {
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })
                    } catch {
                      return 'N/A'
                    }
                  }
                  const roleCounts = messages.reduce<Record<string, number>>((acc, m) => {
                    const r = m.role
                    acc[r] = (acc[r] ?? 0) + 1
                    return acc
                  }, {})
                  const roleSummary = Object.entries(roleCounts)
                    .map(([role, n]) => `${role}: ${n}`)
                    .join(', ')
                  const serverToolLines = config.tools.serverTools
                  const clientToolNames = Object.keys(config.tools.clientTools)
                  return (
                    <div className="space-y-5">
                      <section className="space-y-1">
                        <h3 className="m-0 text-base font-semibold" style={{ color: 'var(--foreground-default)' }}>
                          Thread Information
                        </h3>
                        <div className="space-y-0.5 text-sm" style={{ color: 'var(--foreground-default)' }}>
                          <div><strong>Thread ID:</strong> {session?.threadId || 'None'}</div>
                          <div><strong>Title:</strong> {session?.title || 'N/A'}</div>
                          <div><strong>Created:</strong> {formatTs(session?.createdAt)}</div>
                          <div><strong>Last activity:</strong> {formatTs(session?.updatedAt)}</div>
                          <div><strong>Workspace ID:</strong> {session?.workspaceId || 'N/A'}</div>
                          <div><strong>Project ID:</strong> {metaStr('projectId')}</div>
                          <div><strong>Visibility:</strong> {metaStr('visibility')}</div>
                        </div>
                      </section>
                      <section className="space-y-1">
                        <h3 className="m-0 text-base font-semibold" style={{ color: 'var(--foreground-default)' }}>
                          Thread Configuration
                        </h3>
                        <div className="space-y-0.5 text-sm" style={{ color: 'var(--foreground-default)' }}>
                          <div><strong>Provider:</strong> {metaStr('provider')}</div>
                          <div><strong>Model:</strong> {metaStr('model')}</div>
                          <div><strong>Temperature:</strong> {config.agent.temperature ?? 'N/A'}</div>
                          <div><strong>Max tokens:</strong> {config.agent.maxTokens ?? 'N/A'}</div>
                          <div><strong>Tool policy:</strong> {config.agent.toolPolicy ?? 'N/A'}</div>
                          <div><strong>Agent allowlist (IDs):</strong> {(config.agent.tools ?? []).length > 0 ? (config.agent.tools ?? []).join(', ') : 'N/A'}</div>
                        </div>
                      </section>
                      <section className="space-y-1">
                        <h3 className="m-0 text-base font-semibold" style={{ color: 'var(--foreground-default)' }}>
                          Session Information
                        </h3>
                        <div className="space-y-0.5 text-sm" style={{ color: 'var(--foreground-default)' }}>
                          <div><strong>Session ID:</strong> {session?.id || 'None'}</div>
                          <div><strong>Thread ID:</strong> {session?.threadId || 'None'}</div>
                        </div>
                      </section>
                      <section className="space-y-1">
                        <h3 className="m-0 text-base font-semibold" style={{ color: 'var(--foreground-default)' }}>
                          Agent Configuration
                        </h3>
                        <div className="space-y-0.5 text-sm" style={{ color: 'var(--foreground-default)' }}>
                          <div><strong>Agent mode:</strong> {config.agent.mode}</div>
                          <div><strong>System name:</strong> {config.portal.systemName}</div>
                          <div><strong>Package:</strong> {config.portal.packageName}</div>
                          <div><strong>API endpoint:</strong> {config.portal.apiEndpoint}</div>
                        </div>
                      </section>
                      <section className="space-y-1">
                        <h3 className="m-0 text-base font-semibold" style={{ color: 'var(--foreground-default)' }}>
                          Messages Summary
                        </h3>
                        <div className="space-y-0.5 text-sm" style={{ color: 'var(--foreground-default)' }}>
                          <div><strong>Total messages:</strong> {messages.length}</div>
                          <div><strong>By role:</strong> {roleSummary.length > 0 ? roleSummary : 'none'}</div>
                        </div>
                      </section>
                      <section className="space-y-1">
                        <h3 className="m-0 text-base font-semibold" style={{ color: 'var(--foreground-default)' }}>
                          Features
                        </h3>
                        <div className="space-y-0.5 text-sm" style={{ color: 'var(--foreground-default)' }}>
                          <div><strong>Streaming:</strong> {config.features.streaming ? 'Enabled' : 'Disabled'}</div>
                          <div><strong>File upload:</strong> {config.features.fileUpload ? 'Enabled' : 'Disabled'}</div>
                          <div><strong>Debug:</strong> {config.features.debug ? 'Enabled' : 'Disabled'}</div>
                          <div><strong>Reset conversation:</strong> {config.features.resetConversation ? 'Enabled' : 'Disabled'}</div>
                          <div><strong>Shareable:</strong> {config.features.shareable ? 'Enabled' : 'Disabled'}</div>
                        </div>
                      </section>
                      <section className="space-y-1">
                        <h3 className="m-0 text-base font-semibold" style={{ color: 'var(--foreground-default)' }}>
                          Tools
                        </h3>
                        <div className="space-y-1 text-sm" style={{ color: 'var(--foreground-default)' }}>
                          <div>
                            <strong>Server tools ({serverToolLines.length}):</strong>
                            <ul className="mt-1 list-disc space-y-0.5 pl-5">
                              {serverToolLines.length > 0
                                ? serverToolLines.map((t) => (
                                    <li key={t}>{t}</li>
                                  ))
                                : (
                                    <li>none</li>
                                  )}
                            </ul>
                          </div>
                          <div>
                            <strong>Client tools ({clientToolNames.length}):</strong>
                            <ul className="mt-1 list-disc space-y-0.5 pl-5">
                              {clientToolNames.length > 0
                                ? clientToolNames.map((t) => (
                                    <li key={t}>{t}</li>
                                  ))
                                : (
                                    <li>none</li>
                                  )}
                            </ul>
                          </div>
                        </div>
                      </section>
                    </div>
                  )
                })()}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Debug Info Modal */}
      {showDebugModal && debugInfo && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: 'var(--overlay-background)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="debug-modal-title"
        >
          <div
            className="rounded-lg p-6 shadow-lg max-w-4xl max-h-[80vh] overflow-hidden mx-4 w-full"
            style={{
              backgroundColor: 'var(--background-secondary)',
              color: 'var(--foreground-default)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                id="debug-modal-title"
                className="text-lg font-semibold"
                style={{ color: 'var(--foreground-default)' }}
              >
                Debug Information
              </h3>
              <button
                type="button"
                onClick={() => setShowDebugModal(false)}
                className="korvochat-debug-modal-close transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[60vh] space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Message Details
                  </h4>
                  <div className="text-sm space-y-1" style={{ color: 'var(--foreground-default)' }}>
                    <div><strong>ID:</strong> {debugInfo.messageId}</div>
                    <div><strong>Role:</strong> {debugInfo.role}</div>
                    <div><strong>Timestamp:</strong> {debugInfo.timestamp != null ? new Date(debugInfo.timestamp).toLocaleString(undefined, { 
                      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                      dateStyle: 'medium',
                      timeStyle: 'long'
                    }) : 'N/A'}</div>
                    {debugInfo.createdAt && (
                      <div><strong>Created At:</strong> {new Date(debugInfo.createdAt).toLocaleString(undefined, { 
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        dateStyle: 'medium',
                        timeStyle: 'long'
                      })}</div>
                    )}
                    {debugInfo.updatedAt && (
                      <div><strong>Last Updated:</strong> {new Date(debugInfo.updatedAt).toLocaleString(undefined, { 
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                        dateStyle: 'medium',
                        timeStyle: 'long'
                      })}</div>
                    )}
                    <div><strong>Thread ID:</strong> {debugInfo.threadId || 'N/A'}</div>
                    <div><strong>Session ID:</strong> {debugInfo.sessionId || 'N/A'}</div>
                    <div><strong>Package:</strong> {debugInfo.packageName}</div>
                    <div><strong>System:</strong> {debugInfo.systemName}</div>
                  </div>
                </div>
                
                {debugInfo.role === 'user' ? (
                  <div
                    className="p-3 rounded"
                    style={{
                      backgroundColor: 'var(--background-default)',
                      color: 'var(--foreground-default)',
                    }}
                  >
                    <h4
                      className="font-medium mb-2"
                      style={{ color: 'var(--foreground-default)' }}
                    >
                      Input Data
                    </h4>
                    <div className="text-sm space-y-1" style={{ color: 'var(--foreground-default)' }}>
                      <div><strong>Characters:</strong> {debugInfo.inputData?.characterCount || 0}</div>
                      <div><strong>Words:</strong> {debugInfo.inputData?.wordCount || 0}</div>
                      <div><strong>Lines:</strong> {debugInfo.inputData?.lineCount || 0}</div>
                      <div><strong>Files:</strong> {debugInfo.inputData?.fileCount || 0}</div>
                      <div><strong>Language:</strong> {debugInfo.inputData?.language || 'N/A'}</div>
                      <div><strong>Timezone:</strong> {debugInfo.inputData?.timezone || 'N/A'}</div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="p-3 rounded"
                    style={{
                      backgroundColor: 'var(--background-default)',
                      color: 'var(--foreground-default)',
                    }}
                  >
                    <h4
                      className="font-medium mb-2"
                      style={{ color: 'var(--foreground-default)' }}
                    >
                      Model & Provider
                    </h4>
                    <div className="text-sm space-y-1" style={{ color: 'var(--foreground-default)' }}>
                      <div><strong>Model:</strong> {debugInfo.model || 'N/A'}</div>
                      <div><strong>Provider:</strong> {debugInfo.provider || 'N/A'}</div>
                      <div><strong>Processing Time:</strong> {debugInfo.processingTime?.toFixed(0) || 'N/A'}ms</div>
                    </div>
                  </div>
                )}
              </div>

              {/* User Input Data */}
              {debugInfo.role === 'user' && debugInfo.inputData && (
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Input Analysis
                  </h4>
                  <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    <div><strong>Characters:</strong> {debugInfo.inputData.characterCount}</div>
                    <div><strong>Words:</strong> {debugInfo.inputData.wordCount}</div>
                    <div><strong>Lines:</strong> {debugInfo.inputData.lineCount}</div>
                    <div><strong>Files:</strong> {debugInfo.inputData.fileCount}</div>
                  </div>
                  {debugInfo.inputData.files && debugInfo.inputData.files.length > 0 && (
                    <div className="mt-2">
                      <h5
                        className="font-medium mb-1"
                        style={{ color: 'var(--foreground-default)' }}
                      >
                        Attached Files:
                      </h5>
                      <div className="text-xs space-y-1">
                        {debugInfo.inputData.files.map((file: Record<string, unknown>, index: number) => (
                          <div
                            key={index}
                            className="p-1 rounded border"
                            style={{
                              backgroundColor: 'var(--background-secondary)',
                              borderColor: 'var(--border-default)',
                              color: 'var(--foreground-default)',
                            }}
                          >
                            📎 {String(file.name !== null && file.name !== undefined ? file.name : '')} ({Number(file.size !== null && file.size !== undefined ? file.size : 0)} bytes, {String(file.type !== null && file.type !== undefined ? file.type : '')})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Transmission Data */}
              {debugInfo.role === 'user' && debugInfo.transmissionData && (
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Transmission Data
                  </h4>
                  <div
                    className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    <div><strong>Sent At:</strong> {debugInfo.transmissionData.sentAt != null ? new Date(debugInfo.transmissionData.sentAt).toLocaleString() : '—'}</div>
                    <div><strong>API Endpoint:</strong> {debugInfo.transmissionData.apiEndpoint}</div>
                    <div><strong>Method:</strong> {debugInfo.transmissionData.method}</div>
                    <div><strong>Content Type:</strong> {debugInfo.transmissionData.contentType}</div>
                    <div><strong>Thread Created:</strong> {debugInfo.transmissionData.threadCreated ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              )}

              {/* Token Usage (Assistant only) */}
              {debugInfo.role === 'assistant' && debugInfo.tokens && (
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Token Usage
                  </h4>
                  <div
                    className="grid grid-cols-3 gap-4 text-sm"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    <div><strong>Input:</strong> {debugInfo.tokens.input}</div>
                    <div><strong>Output:</strong> {debugInfo.tokens.output}</div>
                    <div><strong>Total:</strong> {debugInfo.tokens.total}</div>
                  </div>
                  {debugInfo.costCents !== undefined && (
                    <div className="mt-2 text-sm" style={{ color: 'var(--foreground-default)' }}>
                      <strong>Cost:</strong> ${(debugInfo.costCents / 100).toFixed(4)}
                    </div>
                  )}
                </div>
              )}

              {/* Client Environment (User messages) */}
              {debugInfo.role === 'user' && debugInfo.inputData && (
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Client Environment
                  </h4>
                  <div
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    <div><strong>User Agent:</strong> <span className="text-xs break-all">{debugInfo.inputData.userAgent}</span></div>
                    <div><strong>Language:</strong> {debugInfo.inputData.language}</div>
                    <div><strong>Timezone:</strong> {debugInfo.inputData.timezone}</div>
                    <div><strong>Screen:</strong> {window.screen.width}x{window.screen.height}</div>
                  </div>
                </div>
              )}

              {/* Settings (Assistant messages) */}
              {debugInfo.role === 'assistant' && debugInfo.settings && (
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    AI Settings
                  </h4>
                  <div
                    className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    <div><strong>Temperature:</strong> {debugInfo.settings.temperature}</div>
                    <div><strong>Max Tokens:</strong> {debugInfo.settings.maxTokens}</div>
                    <div><strong>Streaming:</strong> {debugInfo.settings.streaming ? 'Yes' : 'No'}</div>
                  </div>
                </div>
              )}

              {/* System Prompt */}
              {debugInfo.systemPrompt && (
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    System Prompt
                  </h4>
                  <div
                    className="text-sm p-2 rounded border max-h-32 overflow-y-auto"
                    style={{
                      backgroundColor: 'var(--background-secondary)',
                      borderColor: 'var(--border-default)',
                      color: 'var(--foreground-default)',
                    }}
                  >
                    {debugInfo.systemPrompt}
                  </div>
                </div>
              )}

              {/* Tool Calls */}
              {debugInfo.toolCalls && debugInfo.toolCalls.length > 0 && (
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: 'var(--background-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <h4
                    className="font-medium mb-2"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Tool Calls
                  </h4>
                  <div
                    className="text-sm p-2 rounded border max-h-32 overflow-y-auto"
                    style={{
                      backgroundColor: 'var(--background-secondary)',
                      borderColor: 'var(--border-default)',
                      color: 'var(--foreground-default)',
                    }}
                  >
                    <pre style={{ color: 'var(--foreground-default)' }}>{JSON.stringify(debugInfo.toolCalls, null, 2)}</pre>
                  </div>
                </div>
              )}

              {/* Raw Debug Data */}
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: 'var(--background-default)',
                  color: 'var(--foreground-default)',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4
                    className="font-medium"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Raw Debug Data
                  </h4>
                  <button
                    type="button"
                    onClick={() => { void navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2)) }}
                    className="korvochat-debug-copy-primary px-2 py-1 text-xs rounded flex items-center space-x-1"
                    title="Copy raw debug data"
                  >
                    <CopyIcon className="w-3 h-3" />
                    <span>Copy</span>
                  </button>
                </div>
                <div
                  className="text-xs p-3 rounded border max-h-48 overflow-y-auto"
                  style={{
                    backgroundColor: 'var(--background-secondary)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <pre className="whitespace-pre-wrap break-words" style={{ color: 'var(--foreground-default)' }}>{JSON.stringify(debugInfo, null, 2)}</pre>
                </div>
              </div>

              {/* Full Chat History */}
              <div
                className="p-3 rounded"
                style={{
                  backgroundColor: 'var(--background-default)',
                  color: 'var(--foreground-default)',
                }}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4
                    className="font-medium"
                    style={{ color: 'var(--foreground-default)' }}
                  >
                    Full Chat History
                  </h4>
                  <button
                    type="button"
                    onClick={() => {
                      const chatHistory = {
                        session: currentSession,
                        config,
                        messages: messages.map(msg => ({
                          id: msg.id,
                          role: msg.role,
                          content: msg.content,
                          timestamp: msg.timestamp,
                          metadata: msg.metadata,
                          files: msg.files,
                          toolCalls: msg.toolCalls,
                          toolResults: msg.toolResults
                        })),
                        debugInfo
                      }
                      void navigator.clipboard.writeText(JSON.stringify(chatHistory, null, 2))
                    }}
                    className="korvochat-debug-copy-success px-2 py-1 text-xs rounded flex items-center space-x-1"
                    title="Copy full chat history with debug data"
                  >
                    <CopyIcon className="w-3 h-3" />
                    <span>Copy History</span>
                  </button>
                </div>
                <div
                  className="text-xs p-3 rounded border max-h-48 overflow-y-auto"
                  style={{
                    backgroundColor: 'var(--background-secondary)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--foreground-default)',
                  }}
                >
                  <div className="space-y-3">
                    {/* Session Info */}
                    <div className="border-b pb-2" style={{ borderColor: 'var(--border-default)' }}>
                      <h5
                        className="font-medium mb-1"
                        style={{ color: 'var(--foreground-default)' }}
                      >
                        Session Information
                      </h5>
                      <pre className="text-xs" style={{ color: 'var(--foreground-default)' }}>{JSON.stringify({
                        sessionId: currentSession?.id,
                        threadId: currentSession?.threadId,
                        packageName: config.portal.packageName,
                        systemName: config.portal.systemName,
                        createdAt: currentSession?.createdAt,
                        updatedAt: currentSession?.updatedAt
                      }, null, 2)}</pre>
                    </div>
                    
                    {/* Messages */}
                    <div className="border-b pb-2" style={{ borderColor: 'var(--border-default)' }}>
                      <h5
                        className="font-medium mb-1"
                        style={{ color: 'var(--foreground-default)' }}
                      >
                        All Messages ({messages.length})
                      </h5>
                      <div className="space-y-2">
                        {messages.map((msg, index) => (
                          <div
                            key={msg.id}
                            className="p-2 rounded"
                            style={{
                              backgroundColor: 'var(--background-default)',
                              color: 'var(--foreground-default)',
                            }}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span
                                className="font-medium text-xs"
                                style={{ color: 'var(--foreground-default)' }}
                              >
                                {index + 1}. {msg.role} - {new Date(msg.timestamp).toLocaleString()}
                              </span>
                              {msg.id === debugInfo.messageId && (
                                <span
                                  className="text-xs px-1 rounded"
                                  style={{
                                    backgroundColor: 'var(--warning-default)',
                                    color: 'var(--warning-foreground)',
                                  }}
                                >
                                  Current
                                </span>
                              )}
                            </div>
                            <div className="text-xs space-y-1" style={{ color: 'var(--foreground-default)' }}>
                              <div><strong>Content:</strong> {msg.content.substring(0, 100)}{msg.content.length > 100 ? '...' : ''}</div>
                              {msg.files && msg.files.length > 0 && (
                                <div><strong>Files:</strong> {msg.files.length} file(s)</div>
                              )}
                              {msg.toolCalls && msg.toolCalls.length > 0 && (
                                <div className="mt-2">
                                  <strong>Tool Calls ({msg.toolCalls.length}):</strong>
                                  <div className="ml-2 mt-1 space-y-2">
                                    {msg.toolCalls.map((tc, tcIndex) => (
                                      <div
                                        key={tcIndex}
                                        className="p-2 rounded border"
                                        style={{
                                          backgroundColor: 'var(--background-secondary)',
                                          borderColor: 'var(--border-default)',
                                          color: 'var(--foreground-default)',
                                        }}
                                      >
                                        <div className="font-mono text-xs" style={{ color: 'var(--foreground-default)' }}>
                                          <div><strong>ID:</strong> {tc.id || 'N/A'}</div>
                                          <div><strong>Name:</strong> {tc.name || (tc as ToolCallWithOpenAiFunction).function?.name || 'N/A'}</div>
                                          <div><strong>Arguments:</strong> {JSON.stringify(tc.arguments || (tc as ToolCallWithOpenAiFunction).function?.arguments || {}, null, 2)}</div>
                                          <div><strong>Result:</strong> {tc.result ? (typeof tc.result === 'string' ? tc.result.substring(0, 200) + (tc.result.length > 200 ? '...' : '') : JSON.stringify(tc.result).substring(0, 200)) : 'N/A'}</div>
                                          <div><strong>Success:</strong> {tc.success !== undefined ? String(tc.success) : 'undefined'}</div>
                                          {tc.error && <div><strong>Error:</strong> {tc.error}</div>}
                                          {tc.progress !== undefined && <div><strong>Progress:</strong> {tc.progress}</div>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {msg.toolResults && msg.toolResults.length > 0 && (
                                <div className="mt-2">
                                  <strong>Tool Results ({msg.toolResults.length}):</strong>
                                  <pre
                                    className="ml-2 mt-1 text-xs p-2 rounded border overflow-x-auto"
                                    style={{
                                      backgroundColor: 'var(--background-secondary)',
                                      borderColor: 'var(--border-default)',
                                      color: 'var(--foreground-default)',
                                    }}
                                  >
                                    {JSON.stringify(msg.toolResults, null, 2)}
                                  </pre>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Configuration */}
                    <div>
                      <h5
                        className="font-medium mb-1"
                        style={{ color: 'var(--foreground-default)' }}
                      >
                        Configuration
                      </h5>
                      <pre
                        className="text-xs"
                        style={{ color: 'var(--foreground-default)' }}
                      >
                        {JSON.stringify({
                          agent: {
                            mode: config.agent.mode,
                            systemPrompt: config.agent.systemPrompt
                          },
                          features: config.features,
                          tools: {
                            serverTools: config.tools.serverTools,
                            clientToolsCount: Object.keys(config.tools.clientTools).length
                          }
                        }, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  )
}
